import React, { useState, useEffect } from "react";
import { NoticeBar } from "./Notices";
import { AskProfWidget } from "./Messages";

// Convert hex colour to "r,g,b" string for rgba() usage
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

// ── DW Blue palette constants ──────────────────────────────────────────────
const GOLD       = "rgb(243, 208, 33)";   // DW signature blue
const GOLD_LIGHT = "#E87F24";   // light blue
const GOLD_PALE  = "#E3F2FD";   // pale blue-white
const GOLD_DEEP  = "rgb(216, 154, 11)";   // deep DW navy
const GOLD_BRIGHT= "#00B0FF";   // bright electric blue
// rgba strings
const GOLDR      = "33,150,243";
const GOLD_LR    = "100,181,246";
const GOLD_PR    = "227,242,253";
const GOLD_BR    = "0,176,255";

// Floating background equations
const BG_EQUATIONS = [
  { text: "E = mc²",                         x: 7,  y: 11, size: 18, opacity: 0.8 },
  { text: "∇²ψ = −(2m/ℏ²)(E − V)ψ",         x: 68, y: 11,  size: 17, opacity: 0.8 },
  { text: "F = −∇V",                          x: 54, y: 80, size: 14, opacity: 0.5 },
  { text: "ΔxΔp ≥ ℏ/2",                      x: 18, y: 62, size: 16, opacity: 0.5 },
  { text: "∮ E · dA = Q / ε₀",               x: 78, y: 44, size: 12, opacity: 0.5 },
  { text: "S = k_B ln Ω",                     x: 38, y: 90, size: 12, opacity: 0.5 },
  { text: "Ĥ |ψ⟩ = E |ψ⟩",                  x: 13, y: 38, size: 14, opacity: 0.5},
  { text: "∇ × B = μ₀ε₀ ∂E/∂t",             x: 42, y: 22, size: 16, opacity: 0.5 },
  { text: "L = T − V",                        x: 84, y: 68, size: 15, opacity: 0.5 },
  { text: "∫ F · ds = ΔKE",                  x: 28, y: 16, size: 12, opacity: 0.5},
  { text: "∇ · E = ρ / ε₀",                  x: 38, y: 52, size: 11, opacity: 0.5 },
  { text: "p = ℏk",                           x: 90, y: 20, size: 15, opacity: 0.5 },
];

// Animated Gaussian wave packet — gold tones
const WaveAnimation = ({ tick }) => {
  const W = 380, H = 180;
  const t = tick * 0.022;
  const psiPoints = [];
  const probPoints = [];
  for (let i = 0; i <= W; i += 2) {
    const x = i;
    const xn = (x - W / 2) / 100;
    const envelope = Math.exp(-xn * xn * 0.9);
    const psi = H / 2 + envelope * Math.sin(x * 0.055 - t) * 52;
    const prob = H * 0.82 - envelope * envelope * 44;
    psiPoints.push(`${x},${psi}`);
    probPoints.push(`${x},${prob}`);
  }
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", width: "100%", height: "auto" }}>
      <defs>
        <linearGradient id="psiGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor={`rgba(${GOLDR},0)`}   />
          <stop offset="30%"  stopColor={`rgba(${GOLDR},0.85)`}/>
          <stop offset="70%"  stopColor={`rgba(${GOLD_LR},0.85)`}/>
          <stop offset="100%" stopColor={`rgba(${GOLD_LR},0)`} />
        </linearGradient>
        <linearGradient id="probGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor={`rgba(${GOLD_LR},0)`}  />
          <stop offset="50%"  stopColor={`rgba(${GOLD_LR},0.4)`}/>
          <stop offset="100%" stopColor={`rgba(${GOLD_LR},0)`}  />
        </linearGradient>
      </defs>
      <polyline points={probPoints.join(" ")} fill="none"
        stroke="url(#probGrad)" strokeWidth="1.5" strokeDasharray="5,3" opacity="0.6" />
      <polyline points={psiPoints.join(" ")} fill="none"
        stroke="url(#psiGrad)" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
};

// ── Young's Double-Slit — 3 auto-cycling modes:
//   WAVE: slit separation animates wide→narrow (Interference → Diffraction)
//   PARTICLE: quantum dots build up interference pattern on screen
const YoungsDSlit = ({ tick }) => {
  const W = 380, H = 205;
  const t = tick * 0.025;

  // Cycle: 380 ticks wave (~19 s), 240 ticks particle (~12 s)
  const WAVE_T = 380;
  const PART_T = 240;
  const TOTAL  = WAVE_T + PART_T;
  const cyclePos   = tick % TOTAL;
  const isParticle = cyclePos >= WAVE_T;

  const CONTENT_H = H - 28;        // bottom 28px for label
  const centerY   = CONTENT_H / 2; // ~88
  const slitX     = W * 0.34;      // ~129
  const screenX   = W * 0.90;      // ~342
  const L         = screenX - slitX; // ~213 px effective distance

  // ── Animate BOTH slit separation AND slit width (wave mode) ──────────────
  // Physics rule:
  //   INTERFERENCE: d large (wide separation), a small (thin slit) → many sharp fringes
  //   DIFFRACTION:  d small (slits close/merge),  a large (wide slit) → sinc² envelope
  const wavePos = isParticle ? 0 : cyclePos;
  // progress: 1 at start (interference), 0 at mid (diffraction), 1 at end
  const progress = 0.5 + 0.5 * Math.cos((wavePos / WAVE_T) * 2 * Math.PI);

  // Slit half-separation: 40px (wide, interference) → 8px (narrow, diffraction)
  const dHalf  = Math.round(8 + 32 * progress);
  // Effective slit width: 7px (thin, interference) → 36px (wide, diffraction)
  const a_phys = Math.round(7 + 29 * (1 - progress));
  // Visual slit half-height mirrors slit width: 4px → 13px
  const visHalf = Math.round(4 + 9 * (1 - progress));

  const slitY1   = centerY - dHalf;
  const slitY2   = centerY + dHalf;
  const isMerged = dHalf <= visHalf + 1;

  // ── Physics: I(y) = sinc²(β) · cos²(δ) ──────────────────────────────
  //   β = π·a·Δy/(λ·L)  ← single-slit diffraction envelope (controlled by a)
  //   δ = π·d·Δy/(λ·L)  ← double-slit interference fringes (controlled by d)
  const lambda = 10;
  const d_phys = 2 * dHalf;

  const screenPts   = [];
  const envelopePts = [];
  for (let py = 1; py < CONTENT_H - 1; py += 2) {
    const dy    = py - centerY;
    const beta  = (Math.PI * a_phys * dy) / (lambda * L);
    const delta = (Math.PI * d_phys  * dy) / (lambda * L);
    const sinc  = Math.abs(beta) < 1e-9 ? 1 : Math.sin(beta) / beta;
    const I     = Math.max(0, sinc * sinc * Math.cos(delta) ** 2);
    screenPts.push({ y: py, i: I });
    if (py % 4 === 1) envelopePts.push({ y: py, e: sinc * sinc });
  }

  // ── Wave ring propagation ─────────────────────────────────────────────
  const maxR = screenX - slitX + 12;
  const rings = [];
  if (!isParticle) {
    for (let r = 0; r < 6; r++) {
      const radius = ((t * 22 + r * 35) % maxR);
      const op = (1 - radius / maxR) * 0.50;
      rings.push({ r: radius, cy: slitY1, op });
      rings.push({ r: radius, cy: slitY2, op });
    }
    if (isMerged) {
      for (let r = 0; r < 6; r++) {
        const radius = ((t * 22 + r * 35) % maxR);
        rings.push({ r: radius, cy: centerY, op: (1 - radius / maxR) * 0.40 });
      }
    }
  }

  // ── Mode label ────────────────────────────────────────────────────────
  let modeLabel, modeLabelColor;
  if (isParticle) {
    modeLabel      = "◆  STREAM OF PARTICLES  —  Quantum Mode";
    modeLabelColor = `rgba(${GOLD_LR},0.80)`;
  } else if (progress > 0.70) {
    modeLabel      = "≋  INTERFERENCE  —  small slit width, wide separation";
    modeLabelColor = `rgba(${GOLDR},0.78)`;
  } else if (progress > 0.30) {
    modeLabel      = "⟺  TRANSITIONING  →  interference to diffraction";
    modeLabelColor = `rgba(${GOLD_LR},0.72)`;
  } else {
    modeLabel      = "≈  DIFFRACTION  —  wide slit width, narrow separation";
    modeLabelColor = `rgba(${GOLD_PR},0.78)`;
  }

  // ── Particle mode: deterministic landing positions (CDF sampling) ─────
  const PCOUNT = 14;
  const PERIOD = 75;
  const d_part = 72;        // fixed wide sep for particle demo
  const a_part = 8;         // thin slits for particle interference demo
  const landingYs = (() => {
    const ptList = [];
    for (let py = 2; py < CONTENT_H - 2; py += 3) {
      const dy    = py - centerY;
      const beta  = (Math.PI * a_part * dy) / (lambda * L);
      const delta = (Math.PI * d_part * dy) / (lambda * L);
      const sinc  = Math.abs(beta) < 1e-9 ? 1 : Math.sin(beta) / beta;
      ptList.push({ y: py, i: Math.max(0, sinc * sinc * Math.cos(delta) ** 2) });
    }
    const total = ptList.reduce((s, p) => s + p.i, 0);
    const pts = [];
    for (let j = 0; j < PCOUNT; j++) {
      const seed = ((j * 137 + 53) % 97) / 97;
      let cum = 0, chosen = ptList[0];
      for (const p of ptList) { cum += p.i / total; if (seed <= cum) { chosen = p; break; } }
      pts.push(chosen.y + ((j * 31 + 7) % 11) - 5);
    }
    return pts;
  })();

  const partSlitY1 = centerY - 36;
  const partSlitY2 = centerY + 36;
  const partProgress = isParticle ? Math.min((cyclePos - WAVE_T) / 60, 1) : 0;

  const particleDots = isParticle ? Array.from({ length: PCOUNT }, (_, i) => {
    const lt    = (tick + (i * PERIOD) / PCOUNT) % PERIOD;
    const upper = i % 2 === 0;
    const tsY   = upper ? partSlitY1 : partSlitY2;
    const tY    = landingYs[i];
    let px, py, alpha;
    if (lt < 32) {
      const prog = lt / 32;
      px    = prog * slitX;
      py    = centerY + (tsY - centerY) * Math.pow(prog, 1.3);
      alpha = 0.90;
    } else {
      const prog = (lt - 32) / (PERIOD - 32);
      px    = slitX + prog * (screenX - slitX);
      py    = tsY + (tY - tsY) * prog;
      alpha = prog < 0.85 ? 0.90 : 0.90 * (1 - (prog - 0.85) / 0.15);
    }
    return { x: px, y: py, alpha };
  }) : [];

  const accumDots = isParticle ? Array.from({ length: PCOUNT * 5 }, (_, j) => {
    const sy = landingYs[j % PCOUNT] + ((j * 17 + j * j) % 13) - 6;
    const nearPt = screenPts.find(p => Math.abs(p.y - sy) < 4);
    return { x: screenX + 2 + ((j * 7) % 8), y: sy,
             a: partProgress * 0.65 * (nearPt ? nearPt.i : 0.2) };
  }) : [];

  // ── Barrier geometry ──────────────────────────────────────────────────
  const topEnd   = Math.max(0, slitY1 - visHalf);
  const midStart = slitY1 + visHalf;
  const midEnd   = slitY2 - visHalf;
  const midH     = midEnd - midStart;
  const botStart = Math.min(CONTENT_H, slitY2 + visHalf);

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", width: "100%", height: "auto" }}>
      <defs>
        <clipPath id="dsRight">
          <rect x={slitX + 2} y={0} width={screenX - slitX + 16} height={CONTENT_H} />
        </clipPath>
        <filter id="goldGlow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="2.5" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* ── Mode label ── */}
      <text x={W / 2} y={H - 8} textAnchor="middle"
        fontSize="8.5" letterSpacing="1.5" fill={modeLabelColor}
        fontFamily="Georgia, serif" fontStyle="italic">
        {modeLabel}
      </text>

      {/* ── Live parameter readout ── */}
      {!isParticle && (
        <>
          <text x={slitX - 6} y={centerY - 6} textAnchor="end" fontSize="6.5"
            fill={`rgba(${GOLDR},0.42)`} fontFamily="Georgia, serif">
            d={d_phys}px
          </text>
          <text x={slitX - 6} y={centerY + 6} textAnchor="end" fontSize="6.5"
            fill={`rgba(${GOLD_LR},0.38)`} fontFamily="Georgia, serif">
            a={a_phys}px
          </text>
        </>
      )}

      {/* ── Incoming plane waves ── */}
      {!isParticle && Array.from({ length: 7 }).map((_, i) => {
        const x = ((t * 24 + i * 34) % slitX);
        return (
          <line key={`pw${i}`} x1={x} y1={0} x2={x} y2={CONTENT_H}
            stroke={`rgba(${GOLDR},1)`} strokeWidth="1.1"
            opacity={(1 - x / slitX) * 0.36} />
        );
      })}

      {/* ── Barrier ── */}
      {topEnd > 0 && (
        <rect x={slitX - 2} y={0} width={4} height={topEnd} fill={GOLD} opacity="0.72" />
      )}
      {!isMerged && midH > 0 && (
        <rect x={slitX - 2} y={midStart} width={4} height={midH} fill={GOLD} opacity="0.72" />
      )}
      {botStart < CONTENT_H && (
        <rect x={slitX - 2} y={botStart} width={4} height={CONTENT_H - botStart}
          fill={GOLD} opacity="0.72" />
      )}

      {/* ── Slit labels ── */}
      {!isMerged ? (
        <>
          <text x={slitX - 5} y={slitY1 + 3} textAnchor="end" fontSize="8"
            fill={`rgba(${GOLDR},0.52)`} fontFamily="Georgia, serif" fontStyle="italic">S₁</text>
          <text x={slitX - 5} y={slitY2 + 3} textAnchor="end" fontSize="8"
            fill={`rgba(${GOLDR},0.52)`} fontFamily="Georgia, serif" fontStyle="italic">S₂</text>
        </>
      ) : (
        <text x={slitX - 5} y={centerY + 3} textAnchor="end" fontSize="8"
          fill={`rgba(${GOLDR},0.52)`} fontFamily="Georgia, serif" fontStyle="italic">S</text>
      )}

      {/* ── Circular waves from slits ── */}
      {!isParticle && (
        <g clipPath="url(#dsRight)">
          {rings.map((ring, i) => (
            <circle key={`r${i}`} cx={slitX} cy={ring.cy} r={ring.r}
              fill="none" stroke={`rgba(${GOLDR},1)`}
              strokeWidth="1.1" opacity={ring.op} />
          ))}
        </g>
      )}

      {/* ── Flying particles ── */}
      {isParticle && particleDots.map((p, i) => (
        <circle key={`p${i}`} cx={p.x} cy={p.y} r="3.5"
          fill={`rgba(${GOLD_LR},${p.alpha})`} filter="url(#goldGlow)" />
      ))}

      {/* ── Screen line ── */}
      <line x1={screenX} y1={0} x2={screenX} y2={CONTENT_H}
        stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />

      {/* ── Wave mode: intensity fringes on screen ── */}
      {!isParticle && screenPts.map((pt, i) => (
        <rect key={`sw${i}`} x={screenX + 1} y={pt.y - 1}
          width={14} height={2.5}
          fill={`rgba(${GOLDR},${(pt.i * 0.95).toFixed(3)})`} />
      ))}

      {/* ── Wave mode: diffraction envelope (dashed line) ── */}
      {!isParticle && envelopePts.map((ep, i) => (
        i > 0 && (
          <line key={`ev${i}`}
            x1={screenX + 1 + envelopePts[i - 1].e * 14}
            y1={envelopePts[i - 1].y}
            x2={screenX + 1 + ep.e * 14}
            y2={ep.y}
            stroke={`rgba(${GOLD_LR},0.38)`}
            strokeWidth="1" strokeDasharray="3,2" />
        )
      ))}

      {/* ── Particle mode: accumulated screen glow ── */}
      {isParticle && accumDots.map((d, i) => (
        <circle key={`ad${i}`} cx={d.x} cy={d.y} r="1.8"
          fill={`rgba(${GOLD_LR},${d.a})`} />
      ))}
      {isParticle && screenPts.map((pt, i) => (
        <rect key={`sp${i}`} x={screenX + 1} y={pt.y - 1}
          width={14} height={2.5}
          fill={`rgba(${GOLDR},${(pt.i * 0.38 * partProgress).toFixed(3)})`} />
      ))}
    </svg>
  );
};



// ── Quantum Probability Amplitude — auto-cycles 4 potential types:
//   Infinite Square Well  /  Barrier (tunneling)  /  −Vδ(x) bound  /  +Vδ(x) scatter
const QuantumPotentials = ({ tick }) => {
  const W = 380, H = 188;
  const CONTENT_H = H - 22;
  const MODE_T   = 220;                    // ticks per mode (~11 s)
  const cyclePos = tick % (MODE_T * 4);
  const mode     = Math.floor(cyclePos / MODE_T);
  const modeProg = (cyclePos % MODE_T) / MODE_T;   // 0 → 1 within each mode

  const tm   = tick * 0.055;              // animation phase
  const yAx  = Math.round(CONTENT_H * 0.60);  // ψ = 0 baseline
  const yTop = 14;
  const AMP  = 34;
  const cx   = W / 2;

  // Infinite well boundaries
  const wL = Math.round(W * 0.22), wR = Math.round(W * 0.78);
  const wW = wR - wL;
  // Barrier boundaries
  const bL = Math.round(W * 0.44), bR = Math.round(W * 0.56);
  const bH = 36;

  const k     = 0.058;   // wave-vector (free region)
  const kappa = 0.028;   // evanescent decay constant

  // ── Compute ψ(x,t) and time-independent |ψ(x)|² ──────────────────────
  const psiPts  = [];
  const probPts = [];

  for (let ix = 0; ix <= W; ix += 2) {
    let psi = 0, prob = 0;

    if (mode === 0) {
      // Infinite square well — ground (n=1) then first excited (n=2)
      const n = modeProg < 0.52 ? 1 : 2;
      if (ix >= wL && ix <= wR) {
        const xi = (ix - wL) / wW;
        psi  = AMP * Math.sin(n * Math.PI * xi) * Math.cos(tm);
        prob = AMP * 0.88 * Math.sin(n * Math.PI * xi) ** 2;   // time-avg |ψ|²
      }

    } else if (mode === 1) {
      // Finite barrier — tunneling
      const R = 0.70, T = 0.40;
      if (ix < bL) {
        // Left: incident + reflected → standing-wave |ψ|² pattern
        psi  = AMP * 0.60 * (Math.cos(k * ix - tm) + R * Math.cos(-k * ix - tm));
        prob = AMP * 0.52 * (1 + R * R + 2 * R * Math.cos(2 * k * ix));
      } else if (ix <= bR) {
        // Inside barrier: evanescent decay
        const xi = (ix - bL) / (bR - bL);
        const d2 = Math.exp(-3.8 * xi);
        psi  = AMP * 0.55 * d2 * Math.cos(tm);
        prob = AMP * 0.52 * d2 * d2;
      } else {
        // Right: transmitted wave
        psi  = AMP * 0.60 * T * Math.cos(k * (ix - bR) - tm);
        prob = AMP * 0.52 * T * T;
      }

    } else if (mode === 2) {
      // Attractive −Vδ(x): single bound state, exponential envelope
      const dx = Math.abs(ix - cx);
      psi  = AMP * Math.exp(-kappa * dx) * Math.cos(tm * 0.30);
      prob = AMP * 0.90 * Math.exp(-2 * kappa * dx);

    } else {
      // Repulsive +Vδ(x): scattering, partial reflection + transmission
      const R = 0.46, T = 0.74;
      if (ix < cx - 2) {
        psi  = AMP * 0.56 * (Math.cos(k * ix - tm) + R * Math.cos(-k * ix - tm));
        prob = AMP * 0.46 * (1 + R * R + 2 * R * Math.cos(2 * k * ix));
      } else if (ix > cx + 2) {
        psi  = AMP * 0.56 * T * Math.cos(k * (ix - cx) - tm);
        prob = AMP * 0.46 * T * T;
      }
    }

    psiPts.push(`${ix},${(yAx - psi).toFixed(1)}`);
    probPts.push(`${ix},${(yAx - Math.max(0, prob)).toFixed(1)}`);
  }

  const probPoly = [...probPts, `${W},${yAx}`, `0,${yAx}`].join(" ");

  const modeLabel = [
    `∞ Square Well  —  Bound State  n = ${modeProg < 0.52 ? 1 : 2}`,
    "Barrier Potential  —  Quantum Tunneling",
    "−Vδ(x)  Attractive  —  Bound State",
    "+Vδ(x)  Repulsive  —  Scattering State",
  ][mode];

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", width: "100%", height: "auto" }}>
      <defs>
        <linearGradient id="qpGrad" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%"   stopColor={`rgba(${GOLDR},0.00)`}/>
          <stop offset="100%" stopColor={`rgba(${GOLDR},0.26)`}/>
        </linearGradient>
      </defs>

      {/* ── x-axis ── */}
      <line x1={0} y1={yAx} x2={W} y2={yAx}
        stroke={`rgba(${GOLDR},0.20)`} strokeWidth="1" strokeDasharray="4,3" />

      {/* ── Potential V(x) visuals ── */}

      {/* Mode 0: Infinite Square Well */}
      {mode === 0 && (
        <>
          {/* Forbidden exterior shading */}
          <rect x={0}      y={yTop} width={wL - 1}    height={yAx - yTop} fill={`rgba(${GOLDR},0.07)`} />
          <rect x={wR + 3} y={yTop} width={W - wR - 3} height={yAx - yTop} fill={`rgba(${GOLDR},0.07)`} />
          {/* Walls */}
          <rect x={wL - 3} y={yTop} width={4} height={yAx - yTop} fill={GOLD} opacity="0.80" />
          <rect x={wR}     y={yTop} width={4} height={yAx - yTop} fill={GOLD} opacity="0.80" />
          {/* ∞ labels */}
          <text x={wL / 2}      y={(yTop + yAx) / 2 + 7} textAnchor="middle" fontSize="22"
            fill={`rgba(${GOLDR},0.14)`} fontFamily="Georgia, serif">∞</text>
          <text x={(wR + W) / 2} y={(yTop + yAx) / 2 + 7} textAnchor="middle" fontSize="22"
            fill={`rgba(${GOLDR},0.14)`} fontFamily="Georgia, serif">∞</text>
          {/* n= tag */}
          <text x={cx} y={yTop + 10} textAnchor="middle" fontSize="8.5"
            fill={`rgba(${GOLDR},0.50)`} fontFamily="Georgia, serif" fontStyle="italic">
            n = {modeProg < 0.52 ? 1 : 2}
          </text>
          {/* x labels */}
          <text x={wL - 1} y={yAx + 12} textAnchor="middle" fontSize="7"
            fill={`rgba(${GOLDR},0.38)`} fontFamily="Georgia, serif">0</text>
          <text x={wR + 2} y={yAx + 12} textAnchor="middle" fontSize="7"
            fill={`rgba(${GOLDR},0.38)`} fontFamily="Georgia, serif">L</text>
        </>
      )}

      {/* Mode 1: Finite Barrier */}
      {mode === 1 && (
        <>
          <rect x={bL} y={yAx - bH} width={bR - bL} height={bH}
            fill={`rgba(${GOLDR},0.12)`} stroke={`rgba(${GOLDR},0.42)`} strokeWidth="1" />
          <text x={(bL + bR) / 2} y={yAx - bH - 4} textAnchor="middle" fontSize="8"
            fill={`rgba(${GOLDR},0.52)`} fontFamily="Georgia, serif" fontStyle="italic">V₀</text>
          {/* Tunneling arrow hint */}
          <text x={(bL + bR) / 2} y={yAx + 14} textAnchor="middle" fontSize="7.5"
            fill={`rgba(${GOLD_LR},0.42)`} fontFamily="Georgia, serif">tunneling →</text>
        </>
      )}

      {/* Mode 2: Attractive delta (spike downward) */}
      {mode === 2 && (
        <>
          <line x1={cx} y1={yAx} x2={cx} y2={yAx + 28}
            stroke={GOLD} strokeWidth="2.5" opacity="0.82" />
          <polygon points={`${cx-5},${yAx+24} ${cx+5},${yAx+24} ${cx},${yAx+34}`}
            fill={GOLD} opacity="0.78" />
          <text x={cx + 12} y={yAx + 32} fontSize="9"
            fill={`rgba(${GOLDR},0.52)`} fontFamily="Georgia, serif" fontStyle="italic">−V₀δ(x)</text>
        </>
      )}

      {/* Mode 3: Repulsive delta (spike upward) */}
      {mode === 3 && (
        <>
          <line x1={cx} y1={yAx} x2={cx} y2={yAx - 32}
            stroke={GOLD} strokeWidth="2.5" opacity="0.82" />
          <polygon points={`${cx-5},${yAx-28} ${cx+5},${yAx-28} ${cx},${yAx-38}`}
            fill={GOLD} opacity="0.78" />
          <text x={cx + 12} y={yAx - 32} fontSize="9"
            fill={`rgba(${GOLDR},0.52)`} fontFamily="Georgia, serif" fontStyle="italic">+V₀δ(x)</text>
        </>
      )}

      {/* ── |ψ(x)|² probability density (filled, time-averaged) ── */}
      <polygon points={probPoly} fill="url(#qpGrad)" opacity="0.55" />

      {/* ── ψ(x, t) real part (animated wave) ── */}
      <polyline points={psiPts.join(" ")} fill="none"
        stroke={`rgba(${GOLDR},0.92)`} strokeWidth="2.2" strokeLinecap="round" />

      {/* ── Legend micro-labels ── */}
      <text x={6} y={yAx - 14} fontSize="7" fill={`rgba(${GOLDR},0.48)`}
        fontFamily="Georgia, serif" fontStyle="italic">Re[ψ]</text>
      <text x={6} y={yAx - 5} fontSize="7" fill={`rgba(${GOLDR},0.35)`}
        fontFamily="Georgia, serif" fontStyle="italic">|ψ|²</text>

      {/* ── Mode label ── */}
      <text x={W / 2} y={H - 5} textAnchor="middle" fontSize="8.5" letterSpacing="1"
        fill={`rgba(${GOLDR},0.72)`} fontFamily="Georgia, serif" fontStyle="italic">
        {modeLabel}
      </text>
    </svg>
  );
};


// ── Quantum Statistics — cycles: N=2 → N=3 → N=4 → Distribution curves
//   Within each N: cycles Classical → Bose-Einstein → Fermi-Dirac
const StatisticsDistributions = ({ tick }) => {
  const W = 380, H = 198;
  const CONTENT_H = H - 22;
  const PHASE_T = 180;  // ticks per N-phase  (~9 s each)
  const TOTAL   = PHASE_T * 4;

  const cyclePos  = tick % TOTAL;
  const phase     = Math.floor(cyclePos / PHASE_T);     // 0,1,2,3
  const phasePos  = cyclePos % PHASE_T;
  const statMode  = phase < 3 ? Math.floor((phasePos / PHASE_T) * 3) : 3;
  // statMode: 0=Classical  1=Bose-Einstein  2=Fermi-Dirac  3=curves

  const N = [2, 3, 4, 4][phase];

  // ── Energy level layout (left panel) ─────────────────────────────────
  const lX1 = 22, lX2 = 183, lMid = (lX1 + lX2) / 2;
  const lY = [152, 116, 80, 44];   // ε₀(bottom) … ε₃(top)
  const pR = 7;

  // Particle configs: { l=level, x=horiz offset, c=color }
  const CFGS = {
    '2-0': [{ l:2,x:-13,c:GOLD }, { l:2,x:13,c:GOLD_LIGHT }],
    '2-1': [{ l:0,x:-9, c:GOLD }, { l:0,x: 9,c:GOLD       }],
    '2-2': [{ l:0,x: 0, c:GOLD }, { l:1,x: 0,c:GOLD       }],
    '3-0': [{ l:1,x:-16,c:GOLD }, { l:2,x:0,c:GOLD_LIGHT  }, { l:1,x:16,c:GOLD_BRIGHT }],
    '3-1': [{ l:0,x:-14,c:GOLD }, { l:0,x:0,c:GOLD        }, { l:0,x:14,c:GOLD        }],
    '3-2': [{ l:0,x:0, c:GOLD  }, { l:1,x:0,c:GOLD        }, { l:2,x: 0,c:GOLD        }],
    '4-0': [{ l:0,x:-14,c:GOLD }, { l:1,x:0,c:GOLD_LIGHT  }, { l:2,x:14,c:GOLD_BRIGHT }, { l:3,x:0,c:GOLD_PALE }],
    '4-1': [{ l:0,x:-18,c:GOLD }, { l:0,x:-6,c:GOLD       }, { l:0,x:6, c:GOLD        }, { l:0,x:18,c:GOLD     }],
    '4-2': [{ l:0,x:0, c:GOLD  }, { l:1,x:0,c:GOLD        }, { l:2,x:0, c:GOLD        }, { l:3,x: 0,c:GOLD     }],
  };
  const cfgKey = `${N}-${Math.min(statMode, 2)}`;
  const parts  = CFGS[cfgKey] || [];

  // ── Distribution curves (phase 3) ────────────────────────────────────
  const pX0 = 18, pX1 = 370, pY0 = CONTENT_H - 18, pY1 = 22;
  const pW = pX1 - pX0, pH = pY0 - pY1;
  const mu_kT = 2.0, maxF = 3.0;

  const bePts = [], fdPts = [], mbPts = [];
  for (let ei = 0; ei <= 100; ei++) {
    const e   = (ei / 100) * 5.5;           // ε/kT from 0 to 5.5
    const ex  = ei / 100;
    const px  = pX0 + ex * pW;
    const arg = e - mu_kT;
    const fd  = 1 / (Math.exp(arg) + 1);
    const mb  = Math.exp(-arg);
    fdPts.push(`${px.toFixed(1)},${(pY0 - Math.min(fd, maxF) / maxF * pH).toFixed(1)}`);
    if (mb < maxF) mbPts.push(`${px.toFixed(1)},${(pY0 - mb / maxF * pH).toFixed(1)}`);
    if (e > mu_kT + 0.08) {
      const be = 1 / (Math.exp(arg) - 1);
      if (be > 0 && be < maxF)
        bePts.push(`${px.toFixed(1)},${(pY0 - be / maxF * pH).toFixed(1)}`);
    }
  }

  // ── Right-panel info strings ──────────────────────────────────────────
  const RINFO = [
    {
      name: "Maxwell-Boltzmann",
      sub:  "(Classical — distinguishable)",
      f1:   "f(ε) = e^{−(ε−μ)/kT}",
      f2:   "nᵢ = 0, 1, 2, … (any)",
      note: "Particles A, B, C… are labeled",
    },
    {
      name: "Bose-Einstein",
      sub:  "(Bosons — symmetric ψ)",
      f1:   "f(ε) = 1 / (e^{(ε−μ)/kT} − 1)",
      f2:   "nᵢ = 0, 1, 2, … (any)",
      note: "Multiple bosons in one state → BEC",
    },
    {
      name: "Fermi-Dirac",
      sub:  "(Fermions — antisymmetric ψ)",
      f1:   "f(ε) = 1 / (e^{(ε−μ)/kT} + 1)",
      f2:   "nᵢ = 0 or 1  (Pauli exclusion)",
      note: "Max one fermion per quantum state",
    },
  ];
  const ri = statMode < 3 ? RINFO[statMode] : null;

  // Right panel x range
  const rX = 195, rMid = rX + 88;

  // ── Bottom label ──────────────────────────────────────────────────────
  const bottomLabel = phase < 3
    ? `${['Maxwell-Boltzmann','Bose-Einstein','Fermi-Dirac'][Math.min(statMode,2)]}  ·  N = ${N} ${statMode===0?'distinguishable':'identical'} particles`
    : "Bose-Einstein  ·  Fermi-Dirac  ·  Maxwell-Boltzmann";

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", width: "100%", height: "auto" }}>

      {/* ══ PHASE 0-2: energy level diagram + info panel ══ */}
      {phase < 3 && (
        <>
          {/* ── Divider ── */}
          <line x1={191} y1={8} x2={191} y2={CONTENT_H - 8}
            stroke={`rgba(${GOLDR},0.14)`} strokeWidth="1" />

          {/* ── Energy levels ── */}
          {lY.map((y, i) => (
            <g key={`lev${i}`}>
              <line x1={lX1} y1={y} x2={lX2} y2={y}
                stroke={`rgba(${GOLDR},0.36)`} strokeWidth="1.3" />
              <text x={lX1 - 4} y={y + 4} textAnchor="end" fontSize="7.5"
                fill={`rgba(${GOLDR},0.46)`} fontFamily="Georgia, serif" fontStyle="italic">
                ε{['₀','₁','₂','₃'][i]}
              </text>
            </g>
          ))}

          {/* ── Particles ── */}
          {parts.map((p, i) => {
            const py = lY[p.l];
            const px = lMid + p.x;
            const rgb = hexToRgb(p.c);
            return (
              <g key={`pt${i}`}>
                <circle cx={px} cy={py} r={pR}
                  fill={`rgba(${rgb},0.90)`}
                  stroke={`rgba(${GOLDR},0.45)`} strokeWidth="0.8" />
                {/* Label classical particles */}
                {statMode === 0 && (
                  <text x={px} y={py + 3.5} textAnchor="middle" fontSize="6.5"
                    fill="rgba(10,7,0,0.80)" fontFamily="Georgia, serif" fontWeight="bold">
                    {['A','B','C','D'][i]}
                  </text>
                )}
              </g>
            );
          })}

          {/* N = … label inside diagram */}
          <text x={lMid} y={CONTENT_H - 6} textAnchor="middle" fontSize="8"
            fill={`rgba(${GOLDR},0.48)`} fontFamily="Georgia, serif" fontStyle="italic">
            N = {N}
          </text>

          {/* ── Right info panel ── */}
          {ri && (
            <>
              {/* Stats name */}
              <text x={rMid} y={32} textAnchor="middle" fontSize="9.5" fontWeight="600"
                fill={`rgba(${GOLDR},0.85)`} fontFamily="Georgia, serif">{ri.name}</text>
              <text x={rMid} y={46} textAnchor="middle" fontSize="7.5"
                fill={`rgba(${GOLDR},0.50)`} fontFamily="Georgia, serif" fontStyle="italic">{ri.sub}</text>

              {/* Separator */}
              <line x1={rX + 6} y1={52} x2={rX + 170} y2={52}
                stroke={`rgba(${GOLDR},0.12)`} strokeWidth="1" />

              {/* Formula */}
              <text x={rMid} y={68} textAnchor="middle" fontSize="8"
                fill={`rgba(${GOLD_LR},0.82)`} fontFamily="Georgia, serif" fontStyle="italic">{ri.f1}</text>
              <text x={rMid} y={84} textAnchor="middle" fontSize="7.5"
                fill={`rgba(${GOLD_LR},0.65)`} fontFamily="Georgia, serif">{ri.f2}</text>

              {/* Separator */}
              <line x1={rX + 6} y1={92} x2={rX + 170} y2={92}
                stroke={`rgba(${GOLDR},0.12)`} strokeWidth="1" />

              {/* Note */}
              <text x={rMid} y={108} textAnchor="middle" fontSize="7.5"
                fill={`rgba(${GOLDR},0.52)`} fontFamily="Georgia, serif" fontStyle="italic">{ri.note}</text>

              {/* Occupation highlight */}
              <rect x={rX + 10} y={120} width={160} height={26} rx="4"
                fill={`rgba(${GOLDR},0.06)`} stroke={`rgba(${GOLDR},0.15)`} strokeWidth="0.8" />
              <text x={rMid} y={134} textAnchor="middle" fontSize="7.5"
                fill={`rgba(${GOLD_LR},0.70)`} fontFamily="Georgia, serif">
                {statMode === 1 ? "Bosons CAN stack in ε₀  →  BEC" : statMode === 2 ? "Fermions fill levels one-by-one" : "Distinguishable: label each particle"}
              </text>
              <text x={rMid} y={146} textAnchor="middle" fontSize="7"
                fill={`rgba(${GOLDR},0.42)`} fontFamily="Georgia, serif" fontStyle="italic">
                {statMode === 1 ? "n₀ = N at T → 0" : statMode === 2 ? "ground state: ε₀,ε₁,ε₂…" : "particle A ≠ particle B"}
              </text>
            </>
          )}
        </>
      )}

      {/* ══ PHASE 3: distribution curves ══ */}
      {phase === 3 && (
        <>
          {/* Axes */}
          <line x1={pX0} y1={pY0} x2={pX1} y2={pY0}
            stroke={`rgba(${GOLDR},0.28)`} strokeWidth="1" />
          <line x1={pX0} y1={pY0} x2={pX0} y2={pY1}
            stroke={`rgba(${GOLDR},0.28)`} strokeWidth="1" />

          {/* Axis labels */}
          <text x={(pX0 + pX1) / 2} y={CONTENT_H - 2} textAnchor="middle" fontSize="8"
            fill={`rgba(${GOLDR},0.45)`} fontFamily="Georgia, serif" fontStyle="italic">ε / kT</text>
          <text x={pX0 - 2} y={pY1 + 4} textAnchor="end" fontSize="8"
            fill={`rgba(${GOLDR},0.45)`} fontFamily="Georgia, serif" fontStyle="italic">⟨n⟩</text>

          {/* μ/kT marker */}
          {(() => {
            const mx = pX0 + (mu_kT / 5.5) * pW;
            return (
              <g>
                <line x1={mx} y1={pY0} x2={mx} y2={pY1}
                  stroke={`rgba(${GOLDR},0.16)`} strokeWidth="1" strokeDasharray="4,3" />
                <text x={mx} y={pY0 + 12} textAnchor="middle" fontSize="7"
                  fill={`rgba(${GOLDR},0.40)`} fontFamily="Georgia, serif" fontStyle="italic">μ</text>
              </g>
            );
          })()}

          {/* MB curve (dashed) */}
          {mbPts.length > 1 && (
            <polyline points={mbPts.join(" ")} fill="none"
              stroke={`rgba(${GOLD_LR},0.72)`} strokeWidth="1.8" strokeDasharray="5,3" />
          )}
          {/* FD curve */}
          {fdPts.length > 1 && (
            <polyline points={fdPts.join(" ")} fill="none"
              stroke={`rgba(${GOLDR},0.92)`} strokeWidth="2.2" />
          )}
          {/* BE curve */}
          {bePts.length > 1 && (
            <polyline points={bePts.join(" ")} fill="none"
              stroke={`rgba(${GOLD_PR},0.88)`} strokeWidth="2.2" />
          )}

          {/* f=1 reference line */}
          {(() => {
            const fy = pY0 - (1 / maxF) * pH;
            return (
              <g>
                <line x1={pX0} y1={fy} x2={pX1} y2={fy}
                  stroke={`rgba(${GOLDR},0.10)`} strokeWidth="1" strokeDasharray="2,4" />
                <text x={pX0 - 3} y={fy + 3} textAnchor="end" fontSize="6.5"
                  fill={`rgba(${GOLDR},0.35)`} fontFamily="Georgia, serif">1</text>
              </g>
            );
          })()}

          {/* Legend — top right */}
          {[
            { pts: bePts, stroke: `rgba(${GOLD_PR},0.88)`, dash: "",    label: "BE  1/(e^{x}−1)", sw: 2.2 },
            { pts: fdPts, stroke: `rgba(${GOLDR},0.92)`,   dash: "",    label: "FD  1/(e^{x}+1)", sw: 2.2 },
            { pts: mbPts, stroke: `rgba(${GOLD_LR},0.72)`, dash: "5,3", label: "MB  e^{−x}",       sw: 1.8 },
          ].map((leg, i) => (
            <g key={`leg${i}`}>
              <line x1={pX1 - 82} y1={pY1 + 14 + i * 16} x2={pX1 - 62} y2={pY1 + 14 + i * 16}
                stroke={leg.stroke} strokeWidth={leg.sw} strokeDasharray={leg.dash} />
              <text x={pX1 - 58} y={pY1 + 18 + i * 16} fontSize="7.5"
                fill={leg.stroke} fontFamily="Georgia, serif" fontStyle="italic">{leg.label}</text>
            </g>
          ))}

          {/* Formula recap */}
          <text x={pX0 + 4} y={pY1 + 14} fontSize="7"
            fill={`rgba(${GOLD_PR},0.60)`} fontFamily="Georgia, serif" fontStyle="italic">f_BE</text>
          <text x={pX0 + 4} y={pY1 + 26} fontSize="7"
            fill={`rgba(${GOLDR},0.55)`} fontFamily="Georgia, serif" fontStyle="italic">f_FD</text>
        </>
      )}

      {/* ── Bottom mode label ── */}
      <text x={W / 2} y={H - 6} textAnchor="middle" fontSize="8.5" letterSpacing="1"
        fill={`rgba(${GOLDR},0.72)`} fontFamily="Georgia, serif" fontStyle="italic">
        {bottomLabel}
      </text>
    </svg>
  );
};

// Bohr atom — gold tones
const BohrAtom = ({ tick }) => {
  const orbitals = [
    { r: 52, ry: 18, speed: 1.0,  tilt:   0, color: GOLD       },
    { r: 78, ry: 27, speed: 0.62, tilt:  60, color: GOLD_LIGHT },
    { r: 104, ry: 36, speed: 0.38, tilt: 120, color: GOLD_BRIGHT},
  ];
  return (
    <svg width="270" height="270" viewBox="0 0 270 270">
      <defs>
        <radialGradient id="nuclGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor={GOLD_PALE}/>
          <stop offset="100%" stopColor={GOLD_DEEP}/>
        </radialGradient>
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur"/>
          <feMerge>
            <feMergeNode in="blur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <filter id="softGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="6" result="blur"/>
          <feMerge>
            <feMergeNode in="blur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      {orbitals.map((o, i) => (
        <ellipse key={i} cx="135" cy="135"
          rx={o.r} ry={o.ry}
          stroke={o.color} strokeWidth="0.8" fill="none" opacity="0.35"
          transform={`rotate(${o.tilt} 135 135)`}
        />
      ))}

      {orbitals.map((o, i) => {
        const a  = ((tick * o.speed) * Math.PI) / 180;
        const tR = (o.tilt * Math.PI) / 180;
        const xe = Math.cos(a) * o.r;
        const ye = Math.sin(a) * o.ry;
        const x  = 135 + xe * Math.cos(tR) - ye * Math.sin(tR);
        const y  = 135 + xe * Math.sin(tR) + ye * Math.cos(tR);
        return (
          <circle key={i} cx={x} cy={y} r="5.5"
            fill={o.color} filter="url(#glow)" opacity="0.95"/>
        );
      })}

      <circle cx="135" cy="135" r="24" fill={`rgba(${GOLDR},0.12)`} filter="url(#softGlow)"/>
      <circle cx="135" cy="135" r="16" fill="url(#nuclGrad)" filter="url(#glow)"/>
      <circle cx="135" cy="135" r="20" fill="none"
        stroke={`rgba(${GOLDR},0.4)`} strokeWidth="0.8"/>
    </svg>
  );
};

// ── Data ──────────────────────────────────────────────────────────────────────

const COURSES = [
  {
    symbol: "∫",
    title: "Classical Mechanics",
    subtitle: "PUC-I Physics",
    desc: "Kinematics, dynamics, gravitation, work-energy theorem, rotational motion and oscillations.",
    color: GOLD,
    rgb: GOLDR,
  },
  {
    symbol: "∇×",
    title: "Electromagnetism",
    subtitle: "PUC-II Physics",
    desc: "Electrostatics, magnetism, electromagnetic induction and Maxwell's equations.",
    color: GOLD_LIGHT,
    rgb: GOLD_LR,
  },
  {
    symbol: "ψ",
    title: "Quantum Mechanics",
    subtitle: "Engineering Physics",
    desc: "Wave-particle duality, Schrödinger equation, atomic models and nuclear physics.",
    color: GOLD_BRIGHT,
    rgb: GOLD_BR,
  },
  {
    symbol: "Σ",
    title: "Thermodynamics",
    subtitle: "Statistical Physics",
    desc: "Laws of thermodynamics, entropy, kinetic theory and statistical mechanics.",
    color: GOLD_PALE,
    rgb: GOLD_PR,
  },
];

const CAPABILITIES = [
  {
    symbol: "◈",
    color: GOLD,
    title: "Interactive Simulations",
    desc: "15+ browser-based physics experiments with real-time parameter control and visual feedback.",
  },
  {
    symbol: "∿",
    color: GOLD_LIGHT,
    title: "AI Physics Tutor",
    desc: "AI tutor powered by Claude, guided to teach through questions — ask anything, anytime.",
  },
  {
    symbol: "≋",
    color: GOLD_BRIGHT,
    title: "Adaptive Assessments",
    desc: "Timed, scheduled quizzes with instant scores and answer review.",
  },
  {
    symbol: "⊕",
    color: GOLD_DEEP,
    title: "Reading Materials",
    desc: "Curated lecture notes, textbook references, and solved examples for every topic.",
  },
];

const READING_MATERIALS = [
  {
    type: "Lecture Notes",
    symbol: "≡",
    color: GOLD,
    rgb: GOLDR,
    items: ["Units & Measurements", "Laws of Motion", "Work, Energy & Power", "Gravitation", "Wave Motion"],
  },
  {
    type: "Question Papers",
    symbol: "?",
    color: GOLD_LIGHT,
    rgb: GOLD_LR,
    items: ["PUC-I Previous Papers", "PUC-II Previous Papers", "Engineering Physics Papers", "Mid-Term Assessments", "Model Papers"],
  },
  {
    type: "Assignments",
    symbol: "✎",
    color: GOLD_BRIGHT,
    rgb: GOLD_BR,
    items: ["Chapter-wise Problems", "Numerical Worksheets", "Derivation Practice", "Conceptual MCQs"],
  },
  {
    type: "Reference Material",
    symbol: "∞",
    color: GOLD_PALE,
    rgb: GOLD_PR,
    items: ["Formula Sheets", "Physical Constants", "Unit Conversions", "Graphs & Diagrams", "Video Lectures"],
  },
];

const STATS = [
  { value: "1,500+", label: "Students",  sub: "RGUKT Basar",            color: GOLD        },
  { value: "15+",    label: "Simulations",       sub: "Interactive Labs",        color: GOLD_LIGHT  },
  { value: "3",      label: "Courses",           sub: "PUC-I · PUC-II · Engg.", color: GOLD_BRIGHT },
  { value: "24/7",   label: "AI Tutor",          sub: "Always Available",        color: GOLD_DEEP   },
];

// ── Quantum Bound States & Scattering — Finite Square Well ───────────────────
// Cycles: n=1 (odd) → n=2 (even) → n=3 (odd) → scattering (E>0)
const QuantumBoundStatesAnim = ({ tick }) => {
  const W = 380, H = 205;
  const CONTENT_H = H - 28;

  // Geometry
  const cX   = W / 2;                     // 190 — well centre
  const eZeroY = Math.round(CONTENT_H * 0.38); // ~67 — V=0 reference line
  const halfL  = 55;                       // well half-width in SVG px
  const wL     = cX - halfL;              // 135 — left wall
  const wR     = cX + halfL;              // 245 — right wall
  const wellBot = Math.round(CONTENT_H * 0.88); // ~154 — well bottom (−V₀)
  const ampMax  = 42;                     // max ψ amplitude in px
  const kappa   = 1.8;                    // visual decay rate outside well

  // Cycle: 240 ticks each → n1 → n2 → n3 → scatter  (~48 s full cycle)
  const PERIOD   = 240;
  const cyclePos = tick % (PERIOD * 4);
  const stateIdx = Math.floor(cyclePos / PERIOD);
  const sp       = cyclePos % PERIOD;
  // Smooth fade in / out at state transitions
  const alpha = Math.min(sp / 28, 1) * (sp > 210 ? Math.max(1 - (sp - 210) / 28, 0) : 1);

  const isScattering = stateIdx === 3;
  const tPhase = sp * 0.07; // traveling-wave time phase

  // k₁ * π inside well — chosen so walls give correct sign & visual node count
  // n=1 even: cos(0.40π·xRel)  → 0 nodes,  wall value ≈ +0.31
  // n=2 odd : sin(0.90π·xRel)  → 1 node,   wall value ≈ ±0.31
  // n=3 even: cos(1.35π·xRel)  → 2 nodes,  wall value ≈ −0.45
  const kList = [0.40, 0.90, 1.35];

  // Build ψ(x) and |ψ|² paths
  const psiPts  = [];
  const probPts = [];

  for (let px = 10; px <= W - 10; px += 2) {
    const xRel = (px - cX) / halfL;   // −1 at wL, +1 at wR
    const isIn = Math.abs(xRel) <= 1.0;
    const xOut = Math.max(0, Math.abs(xRel) - 1.0);

    let psi = 0;

    if (!isScattering) {
      const k1 = kList[stateIdx] * Math.PI;
      if (isIn) {
        psi = stateIdx % 2 === 0
          ? Math.cos(k1 * xRel)               // even states: cosine
          : Math.sin(k1 * xRel);              // odd  states: sine
      } else {
        // Match boundary value, then exponential evanescent tail
        const bv = stateIdx % 2 === 0
          ? Math.cos(k1)
          : (xRel > 0 ? 1 : -1) * Math.abs(Math.sin(k1));
        psi = bv * Math.exp(-kappa * xOut * 2.2);
      }
    } else {
      // Scattering (E > 0): animated traveling wave
      // incident+reflected on left, faster oscillation inside well, transmitted on right
      if (px < wL - 4) {
        psi = Math.cos(1.5 * xRel * 3 - tPhase) + 0.18 * Math.cos(-1.5 * xRel * 3 - tPhase);
      } else if (px <= wR + 4) {
        psi = 0.97 * Math.cos(2.4 * xRel * 2 - tPhase);
      } else {
        psi = 0.97 * Math.cos(1.5 * xRel * 3 - tPhase);
      }
    }

    const svgY     = eZeroY - psi * ampMax;
    const svgProbY = eZeroY - psi * psi * ampMax * 0.75;
    psiPts.push(`${px},${svgY.toFixed(1)}`);
    probPts.push(`${px},${svgProbY.toFixed(1)}`);
  }

  // Energy level y positions inside the well (deeper = lower energy = higher Y)
  const eLevY = [eZeroY + 56, eZeroY + 36, eZeroY + 18];

  const labels = [
    "Bound State  n=1  (even)  —  E < 0",
    "Bound State  n=2  (odd)   —  E < 0",
    "Bound State  n=3  (even)  —  E < 0",
    "Scattering State  E > 0  —  Transmission",
  ];

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", width: "100%", height: "auto" }}>
      <defs>
        <linearGradient id="qbsPsiGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor={`rgba(${GOLDR},0)`}   />
          <stop offset="18%"  stopColor={`rgba(${GOLDR},0.9)`} />
          <stop offset="82%"  stopColor={`rgba(${GOLD_LR},0.9)`}/>
          <stop offset="100%" stopColor={`rgba(${GOLD_LR},0)`} />
        </linearGradient>
        <linearGradient id="qbsWellFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={`rgba(${GOLDR},0.07)`} />
          <stop offset="100%" stopColor={`rgba(${GOLDR},0.01)`} />
        </linearGradient>
        <filter id="qbsGlow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="2.8" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* ── Potential well (U-shape) ── */}
      <rect x={wL} y={eZeroY} width={wR - wL} height={wellBot - eZeroY}
        fill="url(#qbsWellFill)" />
      {/* Left flat top */}
      <line x1={10}  y1={eZeroY} x2={wL}    y2={eZeroY}  stroke={`rgba(${GOLDR},0.55)`} strokeWidth="1.5"/>
      {/* Left wall ↓ */}
      <line x1={wL}  y1={eZeroY} x2={wL}    y2={wellBot} stroke={`rgba(${GOLDR},0.55)`} strokeWidth="1.5"/>
      {/* Well floor */}
      <line x1={wL}  y1={wellBot} x2={wR}   y2={wellBot} stroke={`rgba(${GOLDR},0.55)`} strokeWidth="1.5"/>
      {/* Right wall ↑ */}
      <line x1={wR}  y1={wellBot} x2={wR}   y2={eZeroY}  stroke={`rgba(${GOLDR},0.55)`} strokeWidth="1.5"/>
      {/* Right flat top */}
      <line x1={wR}  y1={eZeroY} x2={W-10}  y2={eZeroY}  stroke={`rgba(${GOLDR},0.55)`} strokeWidth="1.5"/>

      {/* Labels */}
      <text x={13} y={eZeroY - 4} fontSize="7.5" fill={`rgba(${GOLDR},0.42)`}
        fontFamily="Georgia, serif" fontStyle="italic">V = 0</text>
      <text x={W-14} y={eZeroY - 4} textAnchor="end" fontSize="7.5"
        fill={`rgba(${GOLDR},0.42)`} fontFamily="Georgia, serif" fontStyle="italic">V = 0</text>
      <text x={cX} y={wellBot - 5} textAnchor="middle" fontSize="7.5"
        fill={`rgba(${GOLDR},0.35)`} fontFamily="Georgia, serif" fontStyle="italic">−V₀</text>

      {/* ── Energy level dashed line (bound states only) ── */}
      {!isScattering && (
        <g opacity={alpha}>
          <line x1={wL - 20} y1={eLevY[stateIdx]} x2={wR + 20} y2={eLevY[stateIdx]}
            stroke={`rgba(${GOLD_LR},0.65)`} strokeWidth="1.2" strokeDasharray="6,3"/>
          <text x={wR + 24} y={eLevY[stateIdx] + 4} fontSize="8"
            fill={`rgba(${GOLD_LR},0.55)`} fontFamily="Georgia, serif" fontStyle="italic">
            E{['₁','₂','₃'][stateIdx]}
          </text>
        </g>
      )}

      {/* ── |ψ|² probability density (dashed amber) ── */}
      <polyline points={probPts.join(" ")} fill="none"
        stroke={`rgba(${GOLD_BR},0.45)`} strokeWidth="1.2"
        strokeDasharray="3,2" opacity={alpha * 0.8}/>

      {/* ── ψ(x) wavefunction (solid, glowing) ── */}
      <polyline points={psiPts.join(" ")} fill="none"
        stroke="url(#qbsPsiGrad)" strokeWidth="2.4" strokeLinecap="round"
        filter="url(#qbsGlow)" opacity={alpha}/>

      {/* ── Bottom label ── */}
      <text x={W / 2} y={H - 6} textAnchor="middle" fontSize="8.5" letterSpacing="0.8"
        fill={`rgba(${GOLDR},0.72)`} fontFamily="Georgia, serif" fontStyle="italic">
        {labels[stateIdx]}
      </text>
    </svg>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

// ── Physics Story Player ─────────────────────────────────────────────────────
function PhysicsStoryPlayer() {
  const [playing, setPlaying]   = useState(false);
  const [ready,   setReady]     = useState(false);
  const [elapsed, setElapsed]   = useState(0);
  const audioRef = React.useRef(null);

  useEffect(() => {
    const a = new Audio("/simulations/audio/physics_story.mp3");
    a.preload = "metadata";
    a.oncanplaythrough = () => setReady(true);
    a.onerror = () => setReady(false);
    a.ontimeupdate  = () => setElapsed(a.currentTime);
    a.onended = () => { setPlaying(false); setElapsed(0); };
    audioRef.current = a;
    return () => { a.pause(); };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else         { a.play(); setPlaying(true);   }
  };

  const fmt = s => `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`;
  const total = 720; // ~12 min in seconds
  const pct = Math.min(100, (elapsed / total) * 100);

  if (!ready) {
    return (
      <div style={{
        display:"inline-flex", alignItems:"center", gap:12,
        background:"rgba(245,197,24,0.06)",
        border:"1px solid rgba(245,197,24,0.20)",
        borderRadius:14, padding:"14px 22px",
      }}>
        <span style={{fontSize:22}}>🎙️</span>
        <div>
          <div style={{color:"rgba(245,197,24,0.8)", fontWeight:700, fontSize:14}}>
            12-Minute Audio — Coming Soon
          </div>
          <div style={{color:"rgba(255,255,255,0.72)", fontSize:12, marginTop:3}}>
            Full story in Dr. Devaraju's voice · unlocking soon
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background:"rgba(245,197,24,0.05)",
      border:"1px solid rgba(245,197,24,0.22)",
      borderRadius:16, padding:"18px 22px", maxWidth:420,
    }}>
      <div style={{display:"flex", alignItems:"center", gap:14, marginBottom:14}}>
        <button onClick={toggle} style={{
          width:48, height:48, borderRadius:"50%", border:"none",
          background:"linear-gradient(135deg,#f5c518,#d97706)",
          color:"#081428", fontSize:20, cursor:"pointer",
          display:"flex", alignItems:"center", justifyContent:"center",
          flexShrink:0, fontWeight:700,
        }}>{playing ? "⏸" : "▶"}</button>
        <div style={{flex:1}}>
          <div style={{color:"#f5c518", fontWeight:700, fontSize:14, marginBottom:2}}>
            The Story of Physics
          </div>
          <div style={{color:"rgba(255,255,255,0.72)", fontSize:12}}>
            Dr. Devaraju · {fmt(elapsed)} / ~12:00
          </div>
        </div>
      </div>
      {/* progress bar */}
      <div style={{
        height:4, background:"rgba(255,255,255,0.10)",
        borderRadius:4, overflow:"hidden",
      }}>
        <div style={{
          height:"100%", width:`${pct}%`,
          background:"linear-gradient(90deg,#f5c518,#d97706)",
          borderRadius:4, transition:"width 1s linear",
        }}/>
      </div>
    </div>
  );
}

export default function WelcomePage({ onEnter, onGoogleSignIn, onAbout, setPage }) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 50);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{
      minHeight: "100vh",
      background: "#081428",
      fontFamily: "'Segoe UI', 'Inter', system-ui, sans-serif",
      overflowX: "hidden",
      color: "#fff",
      position: "relative",
    }}>

      {/* ── Fixed background equations ───────────────────────────────────── */}
      <div style={{
        position: "fixed", inset: 0,
        pointerEvents: "none", zIndex: 0, overflow: "hidden",
      }}>
        {/* Warm gold ambient glows */}
        <div style={{
          position: "absolute", top: "-10%", left: "-5%",
          width: 600, height: 600,
          background: `radial-gradient(ellipse, rgba(${GOLDR},0.18) 0%, transparent 65%)`,
        }}/>
        <div style={{
          position: "absolute", top: "20%", right: "-8%",
          width: 500, height: 500,
          background: `radial-gradient(ellipse, rgba(${GOLD_LR},0.14) 0%, transparent 65%)`,
        }}/>
        <div style={{
          position: "absolute", bottom: "5%", left: "30%",
          width: 700, height: 400,
          background: `radial-gradient(ellipse, rgba(33,150,243,0.12) 0%, transparent 65%)`,
        }}/>
        <div style={{
          position: "absolute", top: "55%", left: "10%",
          width: 400, height: 400,
          background: `radial-gradient(ellipse, rgba(${GOLD_BR},0.10) 0%, transparent 65%)`,
        }}/>

        {BG_EQUATIONS.map((eq, i) => (
          <div key={i} style={{
            position: "absolute",
            left: `${eq.x}%`,
            top: `${eq.y}%`,
            fontSize: eq.size,
            color: `rgba(${GOLD_LR},${Math.min(eq.opacity * 0.6, 0.10)})`,
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontStyle: "italic",
            userSelect: "none",
            whiteSpace: "nowrap",
            letterSpacing: 0.5,
          }}>{eq.text}</div>
        ))}
        <div style={{
          position: "absolute", top: "35%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800, height: 600,
          background: `radial-gradient(ellipse, rgba(${GOLDR},0.04) 0%, transparent 70%)`,
        }}/>
      </div>

      {/* ── Navigation ───────────────────────────────────────────────────── */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "rgba(8,20,40,0.95)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderBottom: `1px solid rgba(${GOLDR},0.15)`,
      }}>
        <div style={{
          maxWidth: 1140, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 20px", height: 68,
        }}>
          {/* Brand */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src="/professor.jpeg" alt="Physics Hub"
              style={{
                width: 44, height: 44, borderRadius: "50%",
                objectFit: "cover",
                border: `2px solid ${GOLD}`,
              }}
            />
            <div>
              <div style={{
                fontFamily: "Georgia, serif",
                fontWeight: 700, fontSize: 20,
                color: GOLD_PALE, letterSpacing: 1.2,
              }}>Physics Hub</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", letterSpacing: 1.5, textTransform: "uppercase", whiteSpace: "nowrap" }}>RGUKT Basar</div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={onAbout} style={{
              background: "none",
              border: `1px solid rgba(${GOLDR},0.3)`,
              color: `rgba(${GOLD_LR},0.85)`,
              padding: "8px 20px", borderRadius: 5,
              cursor: "pointer", fontSize: 13,
              fontWeight: 500, letterSpacing: 0.3,
              transition: "all 0.2s",
            }}
            onMouseOver={e => { e.currentTarget.style.borderColor = `rgba(${GOLDR},0.7)`; e.currentTarget.style.color = GOLD_LIGHT; }}
            onMouseOut={e  => { e.currentTarget.style.borderColor = `rgba(${GOLDR},0.3)`;  e.currentTarget.style.color = `rgba(${GOLD_LR},0.85)`; }}
            >Faculty</button>

            <button onClick={onGoogleSignIn} style={{
              background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
              border: "none", color: "#040A18",
              padding: "8px 22px", borderRadius: 5,
              cursor: "pointer", fontSize: 13, fontWeight: 700,
              letterSpacing: 0.3, transition: "opacity 0.2s", whiteSpace: "nowrap",
            }}
            onMouseOver={e => e.currentTarget.style.opacity = "0.88"}
            onMouseOut={e  => e.currentTarget.style.opacity = "1"}
            >Sign In</button>
          </div>
        </div>
      </nav>

      {/* ── Notice board (admin-managed) ── */}
      <div style={{ position: "relative", zIndex: 20 }}><NoticeBar /></div>
      <AskProfWidget />

      {/* ── Voice announcement banner ────────────────────────────────────── */}
      <div style={{
        position:"relative", zIndex:10,
        background:"linear-gradient(90deg, rgba(4,14,30,0.97) 0%, rgba(8,24,50,0.97) 50%, rgba(4,14,30,0.97) 100%)",
        borderBottom:"1px solid rgba(79,195,247,0.20)",
        overflow:"hidden",
      }}>
        <style>{`
          @keyframes sweepGlow {
            0%,100%{ opacity:0; transform:translateX(-100%); }
            50%{ opacity:1; transform:translateX(100%); }
          }
          @keyframes pulse {
            0%,100%{ transform:scale(1); }
            50%{ transform:scale(1.12); }
          }
        `}</style>
        <div style={{
          maxWidth:1140, margin:"0 auto",
          display:"flex", alignItems:"center", justifyContent:"center",
          gap:14, padding:"13px 28px", flexWrap:"wrap",
        }}>
          {/* pulsing mic icon */}
          <span style={{ fontSize:18, display:"inline-block" }}>🎙️</span>
          <span style={{
            color:"#e8edf5", fontSize:"0.95rem", fontWeight:600, letterSpacing:0.3,
          }}>
            Listen to Dr. Devaraju explain every simulation
          </span>
        </div>
      </div>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section style={{
        position: "relative", zIndex: 1,
        display: "flex", alignItems: "center",
        padding: "64px 20px 72px",
      }}>
        <div style={{
          maxWidth: 1140, margin: "0 auto", width: "100%",
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))",
          gap: 56, alignItems: "center",
        }}>

          {/* ── Left copy ── */}
          <div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: `rgba(${GOLDR},0.07)`,
              border: `1px solid rgba(${GOLDR},0.22)`,
              borderRadius: 4, padding: "5px 14px", marginBottom: 28,
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: GOLD,
                display: "inline-block",
              }}/>
              <span style={{
                color: GOLD, fontSize: 11, fontWeight: 600,
                letterSpacing: 2, textTransform: "uppercase",
              }}>Excellence in Physics Education</span>
            </div>

            <h1 style={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: "clamp(34px, 4.5vw, 58px)",
              fontWeight: 400, lineHeight: 1.15,
              margin: "0 0 22px", letterSpacing: -0.5,
              color: GOLD_PALE,
            }}>
              Advancing Physics<br/>
              <em style={{
                fontStyle: "italic",
                background: `linear-gradient(90deg, ${GOLD}, ${GOLD_BRIGHT}, ${GOLD_LIGHT}, ${GOLD_PALE})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>Education &amp; Research</em>
            </h1>

            <p style={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              color: "rgba(255,255,255,0.82)", fontSize: 17, lineHeight: 1.8,
              margin: "0 0 36px", maxWidth: 460,
            }}>
              A rigorous, technology-enhanced learning environment for PUC&#8209;I, PUC&#8209;II, and Engineering Physics — featuring interactive simulations, AI-powered tutoring, and research-grade computational tools.
            </p>
            <div style={{ margin: "-20px 0 28px", display: "inline-block", background: "rgba(245,197,24,0.12)", border: "1px solid rgba(245,197,24,0.45)", color: "#FFE082", borderRadius: 8, padding: "8px 14px", fontSize: 14, fontWeight: 600 }}>
              📌 Currently open to Engineering Physics (E1) students. PUC-I / PUC-II access will reopen later.
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <button onClick={onGoogleSignIn} style={{
                background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
                border: "none", color: "#040A18",
                padding: "13px 26px", borderRadius: 8,
                cursor: "pointer", fontSize: 15, fontWeight: 700,
                transition: "transform 0.2s",
                display: "flex", alignItems: "center", gap: 10,
                boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
              }}
              onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseOut={e  => { e.currentTarget.style.transform = "translateY(0)"; }}
              >
                {/* Google G */}
                <span style={{ background: "#fff", borderRadius: "50%", width: 24, height: 24, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57C21.36 18.45 22.56 15.55 22.56 12.25z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                </span>
                Sign in with Google
              </button>

              {setPage && <button onClick={() => setPage("simulations")} style={{
                background: "transparent",
                border: "1.5px solid rgba(255,255,255,0.25)", color: "#e8edf5",
                padding: "13px 24px", borderRadius: 8,
                cursor: "pointer", fontSize: 15, fontWeight: 600,
                transition: "border-color 0.2s",
              }}
              onMouseOver={e => { e.currentTarget.style.borderColor = GOLD; }}
              onMouseOut={e  => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; }}
              >🔬 Try Simulations</button>}
            </div>

            <div style={{
              display: "flex", gap: 24, marginTop: 32, flexWrap: "wrap",
            }}>
              {["Free for RGUKT students", "No App Required", "Works on Mobile"].map(b => (
                <span key={b} style={{
                  color: "rgba(255,255,255,0.6)", fontSize: 13,
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <span style={{ color: GOLD, fontWeight: 700 }}>✓</span>{b}
                </span>
              ))}
            </div>
          </div>

          {/* ── Right: Atom + wave + Young's DSlit ── */}
          <div style={{
            display: "flex", flexDirection: "column",
            alignItems: "center", gap: 20,
          }}>
            {/* Schrödinger label */}
            <div style={{
              fontFamily: "Georgia, serif", fontStyle: "italic",
              fontSize: 17, color: `rgba(${GOLDR},0.60)`,
              letterSpacing: 0.5,
            }}>iℏ ∂ψ/∂t = Ĥψ</div>

            {/* Atom */}
            <BohrAtom tick={tick}/>

            {/* Wave function card */}
            <div style={{
              background: `rgba(${GOLDR},0.04)`,
              border: `1px solid rgba(${GOLDR},0.12)`,
              borderRadius: 10, padding: "18px 22px",
              width: "100%", maxWidth: 380,
            }}>
              <div style={{
                fontSize: 11, color: `rgba(${GOLDR},0.85)`,
                letterSpacing: 2.5, textTransform: "uppercase",
                marginBottom: 12, fontFamily: "Georgia, serif",
              }}>ψ(x, t) — Probability Amplitude</div>
              <WaveAnimation tick={tick}/>
            </div>


          </div>
        </div>
      </section>

      {/* ── Physics in Motion: animation gallery (moved out of the hero) ── */}
      <section style={{ position: "relative", zIndex: 1, padding: "8px 20px 64px" }}>
        <div style={{ maxWidth: 1140, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 22, color: `rgba(${GOLD_LR},0.6)`, fontSize: 11, letterSpacing: 2.5, textTransform: "uppercase" }}>Physics in Motion</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: 20, alignItems: "stretch" }}>
            {/* Young's Double-Slit card */}
            <div style={{
              background: `rgba(${GOLDR},0.04)`,
              border: `1px solid rgba(${GOLDR},0.12)`,
              borderRadius: 10, padding: "18px 22px",
              width: "100%", boxSizing: "border-box",
            }}>
              <div style={{
                fontSize: 11, color: `rgba(${GOLDR},0.85)`,
                letterSpacing: 2.5, textTransform: "uppercase",
                marginBottom: 12, fontFamily: "Georgia, serif",
              }}>Young's Double-Slit — Interference</div>
              <YoungsDSlit tick={tick}/>
            </div>

            {/* Quantum Potentials card */}
            <div style={{
              background: `rgba(${GOLDR},0.04)`,
              border: `1px solid rgba(${GOLDR},0.12)`,
              borderRadius: 10, padding: "18px 22px",
              width: "100%", boxSizing: "border-box",
            }}>
              <div style={{
                fontSize: 11, color: `rgba(${GOLDR},0.85)`,
                letterSpacing: 2.5, textTransform: "uppercase",
                marginBottom: 12, fontFamily: "Georgia, serif",
              }}>Probability Amplitude — Quantum Potentials</div>
              <QuantumPotentials tick={tick}/>
            </div>

            {/* Quantum Statistics card */}
            <div style={{
              background: `rgba(${GOLDR},0.04)`,
              border: `1px solid rgba(${GOLDR},0.12)`,
              borderRadius: 10, padding: "18px 22px",
              width: "100%", boxSizing: "border-box",
            }}>
              <div style={{
                fontSize: 11, color: `rgba(${GOLDR},0.85)`,
                letterSpacing: 2.5, textTransform: "uppercase",
                marginBottom: 12, fontFamily: "Georgia, serif",
              }}>Quantum Statistics — BE · FD · Maxwell-Boltzmann</div>
              <StatisticsDistributions tick={tick}/>
            </div>
          </div>
        </div>
      </section>

      {/* ── Physics Story audio section — hidden until audio is ready ──────
           To re-enable: remove these comment tags and run gen_physics_story.py
      {/* ── Physics Story audio section ──────────────────────────────────── */}
      {false && <section style={{display:"none"}}/>}

      {/* ── Mentors strip ────────────────────────────────────────────────── */}
      <section style={{
        position: "relative", zIndex: 1,
        borderTop: `1px solid rgba(${GOLDR},0.10)`,
        padding: "52px 28px",
        background: `rgba(${GOLDR},0.025)`,
      }}>
        <div style={{
          maxWidth: 900, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexWrap: "wrap", gap: 56,
        }}>
          {/* Label */}
          <div style={{
            fontSize: 11, color: `rgba(${GOLDR},0.85)`,
            letterSpacing: 3, textTransform: "uppercase",
            fontFamily: "Georgia, serif",
            flexBasis: "100%", textAlign: "center", marginBottom: 4,
          }}>Inspired &amp; Guided By</div>

          {/* Chukka Ramaiah Sir */}
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{
              width: 100, height: 100, borderRadius: "50%",
              overflow: "hidden", flexShrink: 0,
              border: `2.5px solid rgba(${GOLDR},0.50)`,
              boxShadow: `0 0 24px rgba(${GOLDR},0.20)`,
            }}>
              <img src="/Ramaiahsir.jpeg" alt="Chukka Ramaiah Sir"
                style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <div>
              <div style={{
                fontFamily: "Georgia, serif", fontWeight: 600,
                color: GOLD_PALE, fontSize: 15, marginBottom: 4,
              }}>Chukka Ramaiah Sir</div>
              <div style={{ color: `rgba(${GOLD_LR},0.70)`, fontSize: 12, lineHeight: 1.7 }}>
                IIT Trainer &amp; Educationist
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{
            width: 1, height: 52,
            background: `rgba(${GOLDR},0.15)`,
          }} />

          {/* Prof Pathak */}
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{
              width: 100, height: 100, borderRadius: "50%",
              overflow: "hidden", flexShrink: 0,
              border: `2.5px solid rgba(${GOLDR},0.50)`,
              boxShadow: `0 0 24px rgba(${GOLDR},0.20)`,
            }}>
              <img src="/Prof Pathak.jpg" alt="Prof A P Pathak"
                style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <div>
              <div style={{
                fontFamily: "Georgia, serif", fontWeight: 600,
                color: GOLD_PALE, fontSize: 15, marginBottom: 4,
              }}>Prof. A. P. Pathak</div>
              <div style={{ color: `rgba(${GOLD_LR},0.70)`, fontSize: 12, lineHeight: 1.7 }}>
                Professor of Physics, University of Hyderabad<br/>
                <span style={{ color: `rgba(${GOLDR},0.55)`, fontSize: 11 }}>
                  PhD Supervisor of Dr G Devaraju
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Press Coverage ───────────────────────────────────────────────── */}
      <section style={{
        position: "relative", zIndex: 1,
        padding: "72px 28px 40px",
        maxWidth: 1140, margin: "0 auto",
      }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: `rgba(${GOLDR},0.08)`,
            border: `1px solid rgba(${GOLDR},0.25)`,
            borderRadius: 4, padding: "5px 16px", marginBottom: 16,
          }}>
            <span style={{ color: GOLD, fontSize: 11, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase" }}>
              Media Coverage
            </span>
          </div>
          <h2 style={{
            fontFamily: "Georgia, serif", fontWeight: 400,
            fontSize: "clamp(24px, 3vw, 36px)",
            color: GOLD_PALE, margin: "0 0 8px", letterSpacing: -0.3,
          }}>📰 In the Press</h2>
          <p style={{ color: `rgba(${GOLD_LR},0.70)`, fontSize: 15, margin: 0 }}>
            Physics Hub featured in Telangana Today
          </p>
        </div>

        <div style={{
          display: "flex", justifyContent: "center", alignItems: "flex-start", gap: 40,
          flexWrap: "wrap",
        }}>
          {/* Newspaper clipping card */}
          <div style={{
            background: "linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))",
            border: `1px solid rgba(${GOLDR},0.22)`,
            borderRadius: 16, overflow: "hidden",
            boxShadow: `0 8px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(${GOLDR},0.10)`,
            maxWidth: 380, width: "100%",
            transition: "transform 0.3s, box-shadow 0.3s",
          }}
          onMouseOver={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 16px 56px rgba(0,0,0,0.6), 0 0 0 1px rgba(${GOLDR},0.25)`; }}
          onMouseOut={e  => { e.currentTarget.style.transform = "translateY(0)";    e.currentTarget.style.boxShadow = `0 8px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(${GOLDR},0.10)`; }}
          >
            {/* Cropped newspaper image — hides phone status bar & browser chrome */}
            <div style={{
              width: "100%", height: 260,
              overflow: "hidden", position: "relative",
            }}>
              <img
                src="/press/Telangana_today.png"
                alt="Telangana Today — Physics Hub Launches"
                style={{
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "center 30%",
                  transform: "scale(1.05)",
                  display: "block",
                }}
              />
              {/* Publication badge overlay */}
              <div style={{
                position: "absolute", top: 12, left: 12,
                background: "rgba(8,20,40,0.88)",
                border: `1px solid rgba(${GOLDR},0.35)`,
                borderRadius: 6, padding: "4px 12px",
                backdropFilter: "blur(6px)",
              }}>
                <span style={{ color: GOLD, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>
                  TELANGANA TODAY
                </span>
              </div>
            </div>

            {/* Card body */}
            <div style={{ padding: "20px 22px 22px" }}>
              <div style={{
                fontSize: 11, color: `rgba(${GOLD_LR},0.65)`,
                letterSpacing: 1.5, textTransform: "uppercase",
                marginBottom: 8,
              }}>April 2026 · City Bureau, Hyderabad</div>
              <h3 style={{
                fontFamily: "Georgia, serif",
                fontSize: 19, fontWeight: 700,
                color: GOLD_PALE, margin: "0 0 10px", lineHeight: 1.35,
              }}>
Physics Hub Launches Innovation in Education
              </h3>
              <p style={{
                color: "rgba(255,255,255,0.70)", fontSize: 13.5,
                lineHeight: 1.7, margin: "0 0 16px",
              }}>
                
              </p>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                paddingTop: 14, borderTop: `1px solid rgba(${GOLDR},0.15)`,
              }}>
                <span style={{ fontSize: 11, color: `rgba(${GOLD_LR},0.55)`, fontStyle: "italic" }}>
                  telanganatoday.com
                </span>
                <span style={{
                  marginLeft: "auto",
                  background: `rgba(${GOLDR},0.12)`,
                  border: `1px solid rgba(${GOLDR},0.25)`,
                  color: GOLD_LIGHT, fontSize: 11, fontWeight: 600,
                  padding: "3px 10px", borderRadius: 20,
                }}>Featured</span>
              </div>
            </div>
          </div>

          {/* Quote pull-out */}
          <div style={{
            maxWidth: 380, width: "100%",
            display: "flex", flexDirection: "column", gap: 20,
            justifyContent: "center",
          }}>
            <div style={{
              background: `rgba(${GOLDR},0.06)`,
              border: `1px solid rgba(${GOLDR},0.18)`,
              borderLeft: `3px solid ${GOLD}`,
              borderRadius: "0 12px 12px 0",
              padding: "20px 22px",
            }}>
              <div style={{ fontSize: 32, color: GOLD, lineHeight: 1, marginBottom: 8, opacity: 0.6 }}>"</div>
              <p style={{
                fontFamily: "Georgia, serif", fontStyle: "italic",
                color: "rgba(255,255,255,0.85)", fontSize: 15.5,
                lineHeight: 1.75, margin: "0 0 12px",
              }}>
                Learning physics from textbooks alone can be challenging and not every student finds it accessible in the traditional mode. The interactive simulations offered through Physics Hub would empower students to understand difficult concepts in an intuitive and engaging manner.
              </p>
              <div style={{ color: `#EFBF04`, fontSize: 12.5, fontWeight: 600 }}>
                — Prof A Govardhan, Vice Chancellor
              </div>
            </div>

            <div style={{
              background: `rgba(${GOLD_BR},0.06)`,
              border: `1px solid rgba(${GOLD_BR},0.20)`,
              borderRadius: 12, padding: "18px 22px",
              display: "flex", gap: 16, alignItems: "center",
            }}>
              <div style={{ fontSize: 32 }}>🎓</div>
              <div>
                <div style={{ color: GOLD_BRIGHT, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                  Launched in 2026
                </div>
                <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 13, lineHeight: 1.5 }}>
                  Portal available at <span style={{ color: GOLD_LIGHT, fontFamily: "monospace", fontSize: 12 }}>physics-portal-app.web.app</span> — used by 1,500+ students at RGUKT Basar
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats strip ──────────────────────────────────────────────────── */}
      <section style={{
        position: "relative", zIndex: 1,
        borderTop: `1px solid rgba(${GOLDR},0.10)`,
        borderBottom: `1px solid rgba(${GOLDR},0.10)`,
        background: `rgba(${GOLDR},0.03)`,
        padding: "40px 28px",
      }}>
        <div style={{
          maxWidth: 1140, margin: "0 auto",
          display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
          gap: 16, textAlign: "center",
        }}>
          {STATS.map(s => (
            <div key={s.label} style={{ padding: "8px 0" }}>
              <div style={{
                fontFamily: "Georgia, serif",
                fontSize: 38, fontWeight: 700,
                color: s.color, lineHeight: 1, marginBottom: 6,
              }}>{s.value}</div>
              <div style={{
                fontSize: 14, color: "rgba(255,255,255,0.7)",
                fontWeight: 600, marginBottom: 3,
              }}>{s.label}</div>
              <div style={{
                fontSize: 12, color: "rgba(255,255,255,0.72)",
                letterSpacing: 1.2,
              }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Courses ──────────────────────────────────────────────────────── */}
      <section style={{
        position: "relative", zIndex: 1,
        padding: "88px 28px",
        maxWidth: 1140, margin: "0 auto",
      }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div style={{
            color: `rgba(${GOLDR},0.65)`, fontSize: 11,
            letterSpacing: 3, textTransform: "uppercase", marginBottom: 14,
          }}>Curriculum</div>
          <h2 style={{
            fontFamily: "Georgia, serif",
            fontSize: "clamp(26px, 3.5vw, 42px)", fontWeight: 400,
            color: GOLD_PALE, margin: "0 0 16px", letterSpacing: -0.3,
          }}>Courses &amp; Disciplines</h2>
          <p style={{
            fontFamily: "Georgia, serif",
            color:"rgba(255,255,255,0.72)", fontSize: 16,
            maxWidth: 520, margin: "0 auto", lineHeight: 1.7,
          }}>
            Rigorous instruction from classical foundations to modern quantum theory
          </p>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 20,
        }}>
          {COURSES.map(c => (
            <div key={c.title} style={{
              background: `rgba(${GOLDR},0.03)`,
              border: `1px solid rgba(${GOLDR},0.10)`,
              borderRadius: 10, padding: "32px 28px",
              position: "relative", overflow: "hidden",
              transition: "all 0.25s", cursor: "default",
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = `rgba(${c.rgb}, 0.07)`;
              e.currentTarget.style.borderColor = `rgba(${c.rgb}, 0.38)`;
              e.currentTarget.style.transform = "translateY(-4px)";
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = `rgba(${GOLDR},0.03)`;
              e.currentTarget.style.borderColor = `rgba(${GOLDR},0.10)`;
              e.currentTarget.style.transform = "translateY(0)";
            }}
            >
              <div style={{
                position: "absolute", right: 14, top: 8,
                fontFamily: "Georgia, serif", fontSize: 78,
                color: c.color, opacity: 0.06,
                lineHeight: 1, userSelect: "none", fontStyle: "italic",
              }}>{c.symbol}</div>

              <div style={{
                width: 46, height: 46, borderRadius: 8,
                background: `rgba(${c.rgb}, 0.10)`,
                border: `1px solid rgba(${c.rgb}, 0.30)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 20,
                fontFamily: "Georgia, serif", fontSize: 22, color: c.color,
              }}>{c.symbol}</div>

              <div style={{
                fontSize: 10, color: c.color,
                letterSpacing: 2.5, textTransform: "uppercase",
                marginBottom: 8, opacity: 0.85,
              }}>{c.subtitle}</div>
              <div style={{
                fontFamily: "Georgia, serif",
                fontSize: 20, fontWeight: 400,
                color: GOLD_PALE, marginBottom: 10,
              }}>{c.title}</div>
              <div style={{
                color:"rgba(255,255,255,0.72)",
                fontSize: 13, lineHeight: 1.7,
              }}>{c.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Capabilities ─────────────────────────────────────────────────── */}
      <section style={{
        position: "relative", zIndex: 1,
        background: `rgba(${GOLDR},0.03)`,
        borderTop: `1px solid rgba(${GOLDR},0.10)`,
        borderBottom: `1px solid rgba(${GOLDR},0.10)`,
        padding: "80px 28px",
      }}>
        <div style={{ maxWidth: 1140, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div style={{
              color: `rgba(${GOLDR},0.65)`, fontSize: 11,
              letterSpacing: 3, textTransform: "uppercase", marginBottom: 14,
            }}>Platform</div>
            <h2 style={{
              fontFamily: "Georgia, serif",
              fontSize: "clamp(26px, 3.5vw, 42px)", fontWeight: 400,
              color: GOLD_PALE, margin: 0, letterSpacing: -0.3,
            }}>Research-Grade Tools</h2>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 18,
          }}>
            {CAPABILITIES.map(c => (
              <div key={c.title} style={{
                display: "flex", gap: 18, padding: "26px 24px",
                borderRadius: 10,
                background: `rgba(${GOLDR},0.03)`,
                border: `1px solid rgba(${GOLDR},0.10)`,
                transition: "all 0.2s",
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = `rgba(${hexToRgb(c.color)},0.08)`;
                e.currentTarget.style.borderColor = `rgba(${hexToRgb(c.color)},0.40)`;
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = `rgba(${GOLDR},0.03)`;
                e.currentTarget.style.borderColor = `rgba(${GOLDR},0.10)`;
              }}
              >
                <div style={{
                  fontFamily: "Georgia, serif",
                  fontSize: 26, color: c.color,
                  minWidth: 36, textAlign: "center",
                  lineHeight: 1, paddingTop: 2,
                }}>{c.symbol}</div>
                <div>
                  <div style={{
                    color: GOLD_LIGHT, fontWeight: 600,
                    fontSize: 15, marginBottom: 7, letterSpacing: 0.2,
                  }}>{c.title}</div>
                  <div style={{
                    color:"rgba(255,255,255,0.72)",
                    fontSize: 13, lineHeight: 1.65,
                  }}>{c.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Voice Introductions ──────────────────────────────────────────── */}
      <section style={{
        position: "relative", zIndex: 1,
        background: "linear-gradient(160deg, rgba(6,20,40,0.95) 0%, rgba(10,28,55,0.98) 100%)",
        borderTop: "1px solid rgba(79,195,247,0.12)",
        borderBottom: "1px solid rgba(79,195,247,0.12)",
        padding: "88px 28px",
        overflow: "hidden",
      }}>
        {/* subtle waveform bg decoration */}
        <svg style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%", opacity:0.04, pointerEvents:"none" }}
          viewBox="0 0 1200 320" preserveAspectRatio="none">
          <polyline fill="none" stroke="#4fc3f7" strokeWidth="2"
            points={Array.from({length:200},(_,i)=>{
              const x=i*6;
              const y=160+Math.sin(i*0.32)*55*Math.exp(-((i-100)**2/3000));
              return `${x},${y}`;
            }).join(' ')}/>
        </svg>

        <div style={{ maxWidth: 1140, margin: "0 auto", position:"relative" }}>
          {/* header */}
          <div style={{ textAlign:"center", marginBottom: 60 }}>
            <div style={{
              display:"inline-flex", alignItems:"center", gap:8,
              background:"rgba(79,195,247,0.08)", border:"1px solid rgba(79,195,247,0.22)",
              borderRadius:20, padding:"5px 16px", marginBottom:18,
            }}>
              <span style={{fontSize:13}}>🎙️</span>
              <span style={{color:"#4fc3f7", fontSize:11, letterSpacing:2.5, textTransform:"uppercase", fontWeight:600}}>
                Voice Narration
              </span>
            </div>
            <h2 style={{
              fontFamily:"Georgia, serif",
              fontSize:"clamp(26px,3.5vw,44px)", fontWeight:400,
              color:"#e8edf5", margin:"0 0 18px", letterSpacing:-0.5,
            }}>
              5-Minute Introductions
            </h2>
            <p style={{
              fontFamily:"Georgia, serif",
              color:"rgba(255,255,255,0.72)", fontSize:17,
              maxWidth:620, margin:"0 auto", lineHeight:1.75,
            }}>
              Before you touch a slider, hear the physics. Every simulation opens with
              a full conceptual walkthrough — slow, deliberate, in the spirit of
              Hewitt's <em>Conceptual Physics</em>.
            </p>
          </div>

          {/* feature row */}
          <div style={{
            display:"grid",
            gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",
            gap:20, marginBottom:56,
          }}>
            {[
              { icon:"🎧", title:"Hear Every Equation", desc:"Formulae read naturally — 'v equals u plus a t' — not raw symbols. Mathematics spoken the way a teacher speaks it." },
              { icon:"🎬", title:"Animated Video Lecture", desc:"Projectile motion plays as a full animated video — ball in flight, vectors, equations — all synchronized to Dr. Devaraju's narration." },
              { icon:"🔊", title:"Slider Narration", desc:"Move any slider and hear an instant Hewitt-style explanation — what changed, why it changed, and what it means physically." },
            ].map(f => (
              <div key={f.title} style={{
                background:"rgba(79,195,247,0.05)",
                border:"1px solid rgba(79,195,247,0.15)",
                borderRadius:14, padding:"28px 26px",
                transition:"all 0.22s",
              }}
              onMouseOver={e=>{ e.currentTarget.style.background="rgba(79,195,247,0.10)"; e.currentTarget.style.borderColor="rgba(79,195,247,0.35)"; }}
              onMouseOut={e=>{ e.currentTarget.style.background="rgba(79,195,247,0.05)"; e.currentTarget.style.borderColor="rgba(79,195,247,0.15)"; }}
              >
                <div style={{fontSize:28, marginBottom:14}}>{f.icon}</div>
                <div style={{color:"#4fc3f7", fontWeight:700, fontSize:15, marginBottom:8}}>{f.title}</div>
                <div style={{color:"rgba(255,255,255,0.72)", fontSize:13, lineHeight:1.7}}>{f.desc}</div>
              </div>
            ))}
          </div>

          {/* simulation cards */}
          <div style={{ textAlign:"center", marginBottom:28 }}>
            <div style={{color:"rgba(79,195,247,0.6)", fontSize:11, letterSpacing:2.5, textTransform:"uppercase"}}>
              10 Simulations
            </div>
          </div>
          <div style={{
            display:"grid",
            gridTemplateColumns:"repeat(auto-fill,minmax(175px,1fr))",
            gap:12,
          }}>
            {[
              { icon:"🚀",  name:"Projectile Motion", dur:"5.1 min", has_video:true,  free:true,  url:"/simulations/plugplay/projectile.html" },
              { icon:"🌀",  name:"Kinematics",        dur:"4.8 min", has_video:false, free:false },
              { icon:"🏢",  name:"Ball from Building",dur:"5.2 min", has_video:false, free:false },
              { icon:"🕰️", name:"Simple Pendulum",   dur:"5.3 min", has_video:false, free:false },
              { icon:"🔄",  name:"Circular Motion",   dur:"5.2 min", has_video:false, free:false },
              { icon:"〰️", name:"Wave Interference", dur:"5.1 min", has_video:false, free:false },
              { icon:"🌊",  name:"Diffraction",       dur:"5.7 min", has_video:false, free:false },
              { icon:"🌀",  name:"Hooke's Law",       dur:"5.6 min", has_video:false, free:false },
              { icon:"⚗️",  name:"Newton's 2nd Law",  dur:"5.6 min", has_video:true,  free:true,  url:"/simulations/plugplay/newtons2nd.html" },
              { icon:"📐",  name:"Vectors",           dur:"5.8 min", has_video:false, free:false },
            ].map(s => (
              s.free ? (
                /* ── FREE card — fully clickable ── */
                <a key={s.name} href={s.url}
                  style={{
                    textDecoration:"none",
                    background:"rgba(79,195,247,0.08)",
                    border:"1.5px solid rgba(79,195,247,0.45)",
                    borderRadius:11, padding:"16px 14px",
                    display:"flex", flexDirection:"column", gap:6,
                    transition:"all 0.2s", cursor:"pointer", position:"relative",
                  }}
                  onMouseOver={e=>{ e.currentTarget.style.background="rgba(79,195,247,0.16)"; e.currentTarget.style.borderColor="rgba(79,195,247,0.70)"; e.currentTarget.style.transform="translateY(-2px)"; }}
                  onMouseOut={e=>{ e.currentTarget.style.background="rgba(79,195,247,0.08)"; e.currentTarget.style.borderColor="rgba(79,195,247,0.45)"; e.currentTarget.style.transform="translateY(0)"; }}
                >
                  <div style={{fontSize:24}}>{s.icon}</div>
                  <div style={{color:"#4fc3f7", fontSize:13, fontWeight:700, lineHeight:1.3}}>{s.name}</div>
                  <div style={{display:"flex", alignItems:"center", gap:5, marginTop:2, flexWrap:"wrap"}}>
                    <span style={{color:"#4fc3f7", fontSize:11}}>🎙️ {s.dur}</span>
                    {s.has_video && (
                      <span style={{
                        background:"rgba(245,197,24,0.18)", color:"#f5c518",
                        fontSize:9, padding:"2px 6px", borderRadius:10,
                        fontWeight:700, letterSpacing:0.5,
                      }}>+ VIDEO</span>
                    )}
                  </div>
                  <div style={{
                    marginTop:6, background:"rgba(79,195,247,0.15)",
                    color:"#4fc3f7", fontSize:11, fontWeight:700,
                    padding:"5px 0", borderRadius:7, textAlign:"center",
                  }}>▶ Watch Introduction</div>
                </a>
              ) : (
                /* ── display-only card (no lock, just not clickable) ── */
                <div key={s.name} style={{
                  background:"rgba(255,255,255,0.025)",
                  border:"1px solid rgba(255,255,255,0.07)",
                  borderRadius:11, padding:"16px 14px",
                  display:"flex", flexDirection:"column", gap:6,
                  opacity:0.9,
                }}>
                  <div style={{fontSize:22}}>{s.icon}</div>
                  <div style={{color:"#c5cdd6", fontSize:13, fontWeight:600, lineHeight:1.3}}>{s.name}</div>
                  <div style={{display:"flex", alignItems:"center", gap:5, marginTop:2}}>
                    <span style={{color:"rgba(79,195,247,0.6)", fontSize:11}}>🎙️ {s.dur}</span>
                  </div>
                </div>
              )
            ))}
          </div>

          {/* coming soon strip */}
          <div style={{
            marginTop:28, textAlign:"center",
            color:"rgba(255,255,255,0.72)", fontSize:12, lineHeight:1.9,
          }}>
            More introductions coming soon — Friction · Moment of Inertia · Pressure &amp; Fluids ·
            Quantum Bound States · Galvanometer · Meter Bridge · Potentiometer · Boat &amp; River · and more
          </div>
        </div>
      </section>

      {/* ── Reading Materials ────────────────────────────────────────────── */}
      <section style={{
        position: "relative", zIndex: 1,
        padding: "88px 28px",
        maxWidth: 1140, margin: "0 auto",
      }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div style={{
            color: `rgba(${GOLDR},0.65)`, fontSize: 11,
            letterSpacing: 3, textTransform: "uppercase", marginBottom: 14,
          }}>Library</div>
          <h2 style={{
            fontFamily: "Georgia, serif",
            fontSize: "clamp(26px, 3.5vw, 42px)", fontWeight: 400,
            color: GOLD_PALE, margin: "0 0 16px", letterSpacing: -0.3,
          }}>Reading Materials</h2>
          <p style={{
            fontFamily: "Georgia, serif",
            color:"rgba(255,255,255,0.72)", fontSize: 16,
            maxWidth: 520, margin: "0 auto", lineHeight: 1.7,
          }}>
            Comprehensive notes, solved papers and reference resources — organised by course and topic
          </p>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: 20,
        }}>
          {READING_MATERIALS.map(mat => (
            <div key={mat.type} style={{
              background: `rgba(${GOLDR},0.03)`,
              border: `1px solid rgba(${GOLDR},0.10)`,
              borderRadius: 10, padding: "28px 26px",
              transition: "all 0.25s", cursor: "default",
              position: "relative", overflow: "hidden",
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = `rgba(${mat.rgb}, 0.06)`;
              e.currentTarget.style.borderColor = `rgba(${mat.rgb}, 0.35)`;
              e.currentTarget.style.transform = "translateY(-4px)";
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = `rgba(${GOLDR},0.03)`;
              e.currentTarget.style.borderColor = `rgba(${GOLDR},0.10)`;
              e.currentTarget.style.transform = "translateY(0)";
            }}
            >
              <div style={{
                position: "absolute", right: 14, top: 6,
                fontFamily: "Georgia, serif", fontSize: 72,
                color: mat.color, opacity: 0.06,
                lineHeight: 1, userSelect: "none",
              }}>{mat.symbol}</div>

              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 7,
                  background: `rgba(${mat.rgb}, 0.10)`,
                  border: `1px solid rgba(${mat.rgb}, 0.30)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "Georgia, serif", fontSize: 18, color: mat.color,
                }}>{mat.symbol}</div>
                <div style={{
                  fontFamily: "Georgia, serif",
                  fontSize: 17, fontWeight: 400, color: GOLD_PALE,
                }}>{mat.type}</div>
              </div>

              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {mat.items.map(item => (
                  <li key={item} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "7px 0",
                    borderBottom: `1px solid rgba(${GOLDR},0.07)`,
                    color: "rgba(255,255,255,0.5)", fontSize: 13,
                  }}>
                    <span style={{
                      width: 5, height: 5, borderRadius: "50%",
                      background: mat.color, opacity: 0.6, flexShrink: 0,
                    }}/>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── Expert Reviews ───────────────────────────────────────────────── */}
      <section style={{
        position: "relative", zIndex: 1,
        borderTop: `1px solid rgba(${GOLDR},0.10)`,
        padding: "60px 28px 56px",
      }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          {/* Heading */}
          <div style={{
            textAlign: "center", marginBottom: 40,
          }}>
            <div style={{
              fontSize: 11, color: `rgba(${GOLDR},0.85)`,
              letterSpacing: 3, textTransform: "uppercase",
              fontFamily: "Georgia, serif", marginBottom: 10,
            }}>What Experts Say</div>
            <div style={{
              fontFamily: "Georgia, serif", fontWeight: 700,
              color: GOLD_PALE, fontSize: 22,
            }}>Reviews &amp; Expert Opinions</div>
          </div>

          {/* Review cards */}
          <div style={{
            display: "flex", flexWrap: "wrap",
            gap: 28, justifyContent: "center",
          }}>
            {[
              {
                name: "Prof. A. P. Pathak",
                title: "Professor of Physics, University of Hyderabad",
                quote: "An impressive initiative by Dr Devaraju — bringing interactive simulations to pre-university students bridges a long-standing gap in physics education.",
                img: "/Prof Pathak.jpg",
              },
              // Add new reviews here as they come in
            ].map((r, i) => (
              <div key={i} style={{
                background: `rgba(${GOLDR},0.04)`,
                border: `1px solid rgba(${GOLDR},0.15)`,
                borderRadius: 16,
                padding: "28px 28px 24px",
                maxWidth: 420, flex: "1 1 340px",
              }}>
                {/* Quote mark */}
                <div style={{
                  fontSize: 52, lineHeight: 1,
                  color: `rgba(${GOLDR},0.20)`,
                  fontFamily: "Georgia, serif",
                  marginBottom: 4,
                }}>"</div>

                <p style={{
                  color: `rgba(${GOLD_LR},0.85)`,
                  fontSize: 14, lineHeight: 1.8,
                  fontStyle: "italic",
                  margin: "0 0 20px",
                }}>{r.quote}</p>

                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: "50%",
                    overflow: "hidden", flexShrink: 0,
                    border: `2px solid rgba(${GOLDR},0.40)`,
                  }}>
                    {r.img
                      ? <img src={r.img} alt={r.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <div style={{
                          width: "100%", height: "100%",
                          background: `rgba(${GOLDR},0.15)`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: GOLD, fontSize: 22, fontFamily: "Georgia, serif",
                        }}>{r.name[0]}</div>
                    }
                  </div>
                  <div>
                    <div style={{
                      fontFamily: "Georgia, serif", fontWeight: 600,
                      color: GOLD_PALE, fontSize: 14,
                    }}>{r.name}</div>
                    <div style={{
                      color: `rgba(${GOLD_LR},0.60)`,
                      fontSize: 11, lineHeight: 1.6,
                    }}>{r.title}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Student Testimonials ─────────────────────────────────────────── */}
      <section style={{
        position: "relative", zIndex: 1,
        borderTop: `1px solid rgba(${GOLDR},0.10)`,
        padding: "72px 28px 64px",
        background: `rgba(${GOLDR},0.02)`,
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {/* Heading */}
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: `rgba(${GOLDR},0.08)`,
              border: `1px solid rgba(${GOLDR},0.25)`,
              borderRadius: 4, padding: "5px 16px", marginBottom: 16,
            }}>
              <span style={{ color: GOLD, fontSize: 11, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase" }}>
                Student Voices
              </span>
            </div>
            <h2 style={{
              fontFamily: "Georgia, serif", fontWeight: 400,
              fontSize: "clamp(26px, 3.5vw, 42px)",
              color: GOLD_PALE, margin: "0 0 12px", letterSpacing: -0.3,
            }}>What Students Say</h2>
            <p style={{
              fontFamily: "Georgia, serif",
              color:"rgba(255,255,255,0.72)", fontSize: 16,
              maxWidth: 500, margin: "0 auto", lineHeight: 1.7,
            }}>
              Feedback from PUC &amp; Engineering Physics students across RGUKT
            </p>
          </div>

          {/* Testimonial cards */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 22,
          }}>
            {[
              {
                quote: "Your clear explanations, patience, and encouragement make learning enjoyable. Your guidance motivates us to think critically and strive for academic excellence.",
                name: "Kasoju Nikhil Chary",
                course: "CSE-C1",
                initial: "K",
                color: GOLDR,
              },
              {
                quote: "Excellent website, Sir! The concepts are presented in a clear and organized manner, making Physics much easier to understand. A valuable learning platform for students.",
                name: "Ravula Anil",
                course: "Engineering Physics",
                initial: "R",
                color: GOLD_LR,
              },
              {
                quote: "Your teaching has not only helped us understand the subjects better but has also inspired us to work harder and believe in ourselves. A lasting impact on all of us.",
                name: "Banoth Siddartha",
                course: "Engineering Physics",
                initial: "B",
                color: GOLD_BR,
              },
              {
                quote: "Having subject-specific resources in one place makes learning Physics much easier. Thank you for going beyond classroom teaching and creating such a valuable resource.",
                name: "Devara Rajender",
                course: "E1 CSE",
                initial: "D",
                color: GOLD_PR,
              },
              {
                quote: "Your app has been a great initiative which has opened up our minds.",
                name: "Bandhela Srinika",
                course: "CSE",
                initial: "B",
                color: GOLDR,
              },
            ].map((t, i) => (
              <div key={i} style={{
                background: `rgba(${GOLDR},0.03)`,
                border: `1px solid rgba(${GOLDR},0.12)`,
                borderRadius: 14, padding: "26px 24px",
                display: "flex", flexDirection: "column", gap: 18,
                transition: "all 0.25s",
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = `rgba(${t.color},0.07)`;
                e.currentTarget.style.borderColor = `rgba(${t.color},0.35)`;
                e.currentTarget.style.transform = "translateY(-4px)";
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = `rgba(${GOLDR},0.03)`;
                e.currentTarget.style.borderColor = `rgba(${GOLDR},0.12)`;
                e.currentTarget.style.transform = "translateY(0)";
              }}
              >
                {/* Stars */}
                <div style={{ color: GOLD, fontSize: 13, letterSpacing: 2 }}>★★★★★</div>

                {/* Quote */}
                <p style={{
                  fontFamily: "Georgia, serif", fontStyle: "italic",
                  color: "rgba(255,255,255,0.78)", fontSize: 14,
                  lineHeight: 1.75, margin: 0, flex: 1,
                }}>"{t.quote}"</p>

                {/* Author */}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                    background: `rgba(${t.color},0.15)`,
                    border: `1.5px solid rgba(${t.color},0.40)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "Georgia, serif", fontWeight: 700,
                    color: `rgba(${t.color},1)`, fontSize: 16,
                  }}>{t.initial}</div>
                  <div>
                    <div style={{
                      fontFamily: "Georgia, serif", fontWeight: 600,
                      color: GOLD_PALE, fontSize: 13, marginBottom: 2,
                    }}>{t.name}</div>
                    <div style={{
                      color: `rgba(${GOLD_LR},0.50)`,
                      fontSize: 11, lineHeight: 1.5,
                    }}>{t.course}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Download as App ──────────────────────────────────────────────── */}
      <section style={{
        position: "relative", zIndex: 1,
        borderTop: `1px solid rgba(${GOLDR},0.10)`,
        padding: "72px 28px",
        background: `linear-gradient(135deg, rgba(${GOLDR},0.05) 0%, rgba(${GOLDR},0.02) 100%)`,
      }}>
        <div style={{ maxWidth: 1140, margin: "0 auto" }}>
          {/* Header */}
          <div style={{
            textAlign: "center", marginBottom: 60,
          }}>
            <div style={{
              fontSize: 11, color: `rgba(${GOLDR},0.85)`,
              letterSpacing: 3, textTransform: "uppercase",
              fontFamily: "Georgia, serif", marginBottom: 12,
            }}>Quick Access</div>
            <h2 style={{
              fontFamily: "Georgia, serif",
              fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 400,
              color: GOLD_PALE, margin: "0", letterSpacing: -0.3,
              lineHeight: 1.2, marginBottom: 16,
            }}>
              Download Physics Hub as an App
            </h2>
            <p style={{
              color: `rgba(${GOLD_LR},0.70)`, fontSize: 15,
              maxWidth: 700, margin: "0 auto", lineHeight: 1.7,
            }}>
              Access interactive physics simulations anytime, anywhere. Install on your iOS or Android device in seconds.
            </p>
          </div>

          {/* iOS Instructions */}
          <div style={{
            maxWidth: 480,
            margin: "0 auto",
          }}>
            <div style={{
              background: `rgba(${GOLDR},0.06)`,
              border: `1.5px solid rgba(${GOLDR},0.20)`,
              borderRadius: 16, padding: "40px 32px",
              transition: "all 0.3s",
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = `rgba(${GOLDR},0.10)`;
              e.currentTarget.style.borderColor = `rgba(${GOLDR},0.35)`;
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = `rgba(${GOLDR},0.06)`;
              e.currentTarget.style.borderColor = `rgba(${GOLDR},0.20)`;
              e.currentTarget.style.transform = "translateY(0)";
            }}
            >
              <div style={{
                fontSize: 44, marginBottom: 20, textAlign: "center",
              }}>📱</div>

              <h3 style={{
                fontFamily: "Georgia, serif", fontWeight: 600,
                color: GOLD_LIGHT, fontSize: 20, marginBottom: 24,
                textAlign: "center",
              }}>iOS (Safari)</h3>

              <ol style={{
                color: `rgba(${GOLD_LR},0.80)`, fontSize: 14,
                lineHeight: 2.2, margin: 0, paddingLeft: 20,
                listStylePosition: "outside",
              }}>
                <li style={{
                  marginBottom: 12,
                }}>
                  <strong style={{ color: GOLD_LIGHT }}>Open Safari</strong> and visit <span style={{
                    background: `rgba(${GOLDR},0.15)`, padding: "2px 6px",
                    borderRadius: 4, fontFamily: "monospace", fontSize: 12,
                  }}>physics-portal-app.web.app</span>
                </li>
                <li style={{
                  marginBottom: 12,
                }}>
                  <strong style={{ color: GOLD_LIGHT }}>Tap Share</strong> ⬆️ (bottom right corner)
                </li>
                <li style={{
                  marginBottom: 12,
                }}>
                  <strong style={{ color: GOLD_LIGHT }}>Tap "Add to Home Screen"</strong>
                </li>
                <li style={{
                  marginBottom: 12,
                }}>
                  <strong style={{ color: GOLD_LIGHT }}>Customize the name</strong> (optional)
                </li>
                <li style={{
                  marginBottom: 0,
                }}>
                  <strong style={{ color: GOLD_LIGHT }}>Tap "Add"</strong> — Done! 🎉
                </li>
              </ol>

              <div style={{
                marginTop: 24, padding: "12px 16px",
                background: `rgba(${GOLDR},0.08)`,
                borderLeft: `3px solid ${GOLD}`,
                borderRadius: 4,
              }}>
                <div style={{
                  color: `rgba(${GOLD_LR},0.65)`, fontSize: 12,
                }}>
                  ℹ️ Works on iPhone, iPad, and iPod Touch
                </div>
              </div>
            </div>
          </div>

          {/* Info box */}
          <div style={{
            marginTop: 48, padding: "20px 28px",
            background: `rgba(${GOLDR},0.04)`,
            border: `1px solid rgba(${GOLDR},0.12)`,
            borderRadius: 12,
            textAlign: "center",
          }}>
            <div style={{
              color: `rgba(${GOLD_LR},0.75)`, fontSize: 13, lineHeight: 1.8,
            }}>
              <span style={{ color: GOLD, fontWeight: 700 }}>✓</span> No app store account needed for iOS  &nbsp;•&nbsp;
              <span style={{ color: GOLD, fontWeight: 700 }}>✓</span> Add to your home screen &nbsp;•&nbsp;
              <span style={{ color: GOLD, fontWeight: 700 }}>✓</span> Works like a native app
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer style={{
        position: "relative", zIndex: 1,
        borderTop: `1px solid rgba(${GOLDR},0.10)`,
        padding: "28px 28px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12,
        maxWidth: 1140, margin: "0 auto",
      }}>
        <div style={{
          fontFamily: "Georgia, serif",
          color:"rgba(255,255,255,0.72)", fontSize: 13,
        }}>
          © 2026 Physics Hub · Built with ❤️ for Physics Educators & Students
        </div>
        <div style={{ color:"rgba(255,255,255,0.72)", fontSize: 12 }}>
          Developed by{" "}
          <span style={{ color: `rgba(${GOLDR},0.60)` }}>Dr. G. Devaraju</span>
        </div>
      </footer>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1;   transform: scale(1);   }
          50%       { opacity: 0.3; transform: scale(0.6); }
        }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position:  400px 0; }
        }
        *, *::before, *::after { box-sizing: border-box; }
      `}</style>
    </div>
  );
}
